import {
  needsSuppliedValue,
  answerToleranceMode, answerSymmetry,
  angleModeFor, buildFitbWordTrigger, FITB_BLANK_EMPTY, type GradingMode,
  cardinalPointFromDegrees, computeRotationAcceptedRanges, computeRotationDefaultIncorrectGaps,
  computeTiltAcceptedRanges, computeTiltDefaultIncorrectGaps, TILT_DOMAIN_HI, TILT_DOMAIN_LO,
  type TiltConditionType, type TiltInterval,
  computeCompassAcceptedRanges, computeCompassDefaultIncorrectGaps,
  computeAcceptedRanges as computeAcceptedRangesShared, computeDefaultIncorrectGaps,
  computeGpsDefaultIncorrectGaps, evaluateFormulaRaw, exactFeedbackPointsInGap, hasVariableChip,
  parseSortPartitionTrigger,
  isGpsDefaultIncorrectUnreachable, isReachable, resolveTriggerValues,
  type DropdownOption, type FeedbackState, type FitbBlankWord, type GPSInterval,
  type RotationConditionType, type RotationInterval, type CompassConditionType, type CompassPoint,
  type AngleMode, type KeywordOption, type MCQOption, type MCQReachability,
  type NumericalConditionType, type NumericalToleranceMode, type NumericalUnitOption,
  type ScaleValue,
} from '@agavi/blocks';
import { atPlaceExponential, boundAtSigFigs, countSigFigs, lastSignificantPlace, uncertaintyAsked, uncertaintyDecimalsFor } from '@agavi/blocks';
import React from 'react';
import { Trans } from 'react-i18next';

import { chipifyFormulaValues, FormulaWithValues } from '@/components/task/formula-field';
import { stripHtmlForLabel } from '@/components/task/formula-plaintext';
import { MediaKindIcon } from '@/components/task/media-kind-icon';
import { fmtDegrees, fmtFixed, fmtLocale } from '@/components/task/number-format';
import { isValidUnit } from '@/components/task/unit-utils';
import { PreviewHtml, useBlockPreview } from '@/components/task/variable-chip-preview';
import { sortBinLabelText, sortItemLabelText,
  gpsGapChipLabel,
  answerExprTexts, fitbWordLabelText, gpsCorrectChipLabel, gpsIncorrectChipLabel,
  orderingCustomOrderSequenceText, orderingItemLabelText, scaleValueLabel,
} from '@/lib/block-state-labels';
import {
  cutChip, cutSymbol, intervalSourceId, isCutCondition, joinChipGroups, joinChips,
  type ChipEvaluation, type ChipRoll, type ConditionChip, type ConditionChips,
} from '@/lib/condition-chips';

/** Scale's reachability, as the block computes it. Structural rather than imported so this module
 *  never has to reach back into a block for a type. */
/** Dropdown's reachability, structurally — see the note on ScaleReachability. */
interface DropdownReachability {
  correctOptions: DropdownOption[];
  uncoveredWrong: DropdownOption[];
}

/**
 * GPS's EDITOR data, whose coordinate fields are strings — the package's `GPSEditorData` is the
 * evaluated shape with numbers. Structural here for the same reason as the rest: this module never
 * reaches back into a block for a type.
 */
interface GPSEditorData {
  targetLat: string;
  targetLng: string;
  toleranceRadius: string;
  feedback?: { states?: { type?: string; trigger: string }[] };
}

/**
 * Numerical's EDITOR data, as far as the chips and the bar read it.
 *
 * Structural rather than the block's own exported interface, for the reason every shape here is:
 * importing it would point this module back at a block. A subset is also honest about what the
 * derivation depends on — nothing here cares about the settings drawer or the math-error slots.
 */
/** One exact answer row, as the editor holds it. */
type NumericalExactAnswer = NumericalChipData['answers'][number];

interface NumericalChipData {
  conditionType: NumericalConditionType;
  answers: { id: string; value: string; toleranceValue: string; toleranceAbove: string; toleranceBelow: string;
    toleranceSymmetry?: 'symmetric' | 'asymmetric'; toleranceMode?: 'absolute' | 'percentage' }[];
  betweenAnswers: { id: string; low: string; high: string }[];
  threshold: string;
  /** @deprecated Fallback only — resolve through `answerToleranceMode`. */
  toleranceMode?: NumericalToleranceMode;
  /** @deprecated Fallback only — resolve through `answerSymmetry`. */
  toleranceSymmetry?: 'symmetric' | 'asymmetric';
  sigFigsRequired: boolean;
  sigFigsCount: number;
  requireExponentialNotation: boolean;
  unitMode: 'none' | 'display' | 'withUnits';
  unitLabel: string;
  canonicalUnit: string;
  unitOptions: NumericalUnitOption[];
  angleMode: AngleMode;
  /** The error box. An accepted answer is BOTH numbers when one is asked for, so the chip that
   *  states what Correct accepts has to say so — `5 m` for a block wanting `5.0 ± 0.1 m` is half
   *  the answer presented as the whole of it. */
  uncertaintyRequired?: boolean;
  uncertaintyValue?: string;
  /** Decides how many decimals the pair is printed to — see `uncertaintyDecimalsFor`. */
  uncertaintySigFigs?: number;
  integerOnly?: boolean;
}

/** Angle's EDITOR data — raw strings, as Compass's. */
interface RotationEditorData {
  /** Per-field angle modes, keyed by `fieldId` — see `angleModeFor`. */
  angleModes?: Record<string, 'degrees' | 'radians'>;
  conditionType: RotationConditionType;
  answers: { id: string; value: string; toleranceValue: string; toleranceAbove: string; toleranceBelow: string;
    toleranceSymmetry?: 'symmetric' | 'asymmetric'; toleranceMode?: 'absolute' | 'percentage' }[];
  betweenAnswers: { id: string; low: string; high: string }[];
  /** @deprecated Fallback only — resolve through `answerSymmetry`. */
  toleranceSymmetry?: 'symmetric' | 'asymmetric';
  threshold: string;
}

/** Tilt's EDITOR data — raw strings, formulas unresolved. Structural, as everything here is. */
interface TiltEditorData {
  /** Per-field angle modes, keyed by `fieldId` — see `angleModeFor`. */
  angleModes?: Record<string, 'degrees' | 'radians'>;
  conditionType: TiltConditionType;
  answers: { id: string; value: string; toleranceValue: string; toleranceAbove: string; toleranceBelow: string;
    toleranceSymmetry?: 'symmetric' | 'asymmetric'; toleranceMode?: 'absolute' | 'percentage' }[];
  betweenAnswers: { id: string; low: string; high: string }[];
  /** @deprecated Fallback only — resolve through `answerSymmetry`. */
  toleranceSymmetry?: 'symmetric' | 'asymmetric';
  threshold: string;
}

/** The comparison symbols an angle condition can carry, for the unresolved-formula chips. */
/** Compass's EDITOR data — raw strings, formulas unresolved. Structural, as everything here is. */
interface CompassEditorData {
  /** Per-field angle modes, keyed by `fieldId` — see `angleModeFor`. */
  angleModes?: Record<string, 'degrees' | 'radians'>;
  conditionType: CompassConditionType;
  answers: { id: string; value: string; toleranceValue: string; toleranceAbove: string; toleranceBelow: string;
    toleranceSymmetry?: 'symmetric' | 'asymmetric'; toleranceMode?: 'absolute' | 'percentage' }[];
  betweenAnswers: { id: string; low: string; high: string }[];
  /** @deprecated Fallback only — resolve through `answerSymmetry`. */
  toleranceSymmetry?: 'symmetric' | 'asymmetric';
}

/** An Ordering item, structurally — see the note on ScaleReachability. */
interface OrderingItem { id: string; text: string; formula?: string; media?: unknown }

interface ScaleReachability {
  correctValues: { value: number; role?: string }[];
  uncoveredWrong: { value: number; role?: string }[];
}

/**
 * Each block's condition chips, in `lib/` where a non-block surface can reach them.
 *
 * Every block already has ONE chip derivation shared by its own two surfaces — the feedback panel
 * and the live-test bar. What it did not have was a home outside the component, and that is a
 * different problem with its own failure: `lib/` could not call it, so anything else that needed a
 * block's answer language wrote its own. Flow did exactly that, and its Compass catch-all said
 * "Anything but 320° to 40°" while the block's own card said "41° to 319°" — one state, two
 * sentences, and the teacher could not find the feedback Flow was naming.
 *
 * Fixing Flow's labels closed that instance. This file closes the mechanism: the next surface that
 * wants a block's chips — a readiness report, an attempt-data export, the accessibility scan — can
 * have the real ones instead of re-deriving a near-miss.
 *
 * `.tsx`, not `.ts`, and deliberately: a chip's `label` is a NODE. An option can be a formula, a
 * media reference or a variable chip, none of which survive being flattened to a string, which is
 * the whole reason `ConditionChips` exists beside `stateChipLabel` (see `condition-chips.ts`).
 *
 * Moved one block at a time, each with the full suite green, so a half-finished refactor never
 * leaves two live copies of one derivation. The blocks that have not moved yet keep theirs; the
 * import direction is the point, so nothing here may import from a block component.
 */

/** The sourceId standing for "a word nobody listed" — not an option, so it has no id of its own. */
export const KEYWORD_UNLISTED = '__unlisted__';

export function keywordOptionLabelText(option: { text?: unknown }): string {
  return String(option.text ?? '');
}

/**
 * Keyword is an OPEN domain: a student types anything, so the complement can never be enumerated.
 * The chips name the listed words and keep a generic catch-all beside them.
 */
export function keywordChips(
  kind: 'correct' | 'incorrect',
  uncovered: KeywordOption[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  /**
   * The block's grading mode, for the CATCH-ALL's list.
   *
   * Keyword's answer space is OPEN, so unlike MCQ its catch-all can never be closed off: whatever
   * words are listed, a student can always type something else. "Any unlisted word" therefore
   * stays in the row in every mode — this only neutralises the tone of the listed chips beside
   * it, since roles are not consulted outside graded.
   */
  mode: GradingMode = 'graded',
): ConditionChips {
  const mono = (opt: KeywordOption) => <span className='font-mono'>{keywordOptionLabelText(opt)}</span>;
  if (kind === 'correct') {
    if (uncovered.length === 0) return [{ kind: 'unreachable' }];
    return joinChips(uncovered.map(opt => ({
      kind: 'chip' as const,
      label: mono(opt),
      text: keywordOptionLabelText(opt),
      evaluation: 'correct' as const,
      sourceId: opt.id,
    })), 'or');
  }
  return joinChips([
    ...uncovered.map(opt => ({
      kind: 'chip' as const,
      label: mono(opt),
      text: keywordOptionLabelText(opt),
      // A neutral-role keyword is not WRONG, it is flagged — the legend has always toned these
      // separately and the panel's own `nonCorrectChipStyle` did the same.
      evaluation: (mode !== 'graded' ? 'neutral'
        : opt.role === 'neutral' ? 'neutral' : 'incorrect') as ChipEvaluation,
      sourceId: opt.id,
    })),
    {
      kind: 'chip' as const,
      label: t('blocks.keyword.anyUnlistedWord'),
      // Neutral outside graded, like the listed chips beside it. An unlisted word is not a WRONG
      // word in a mode with no right answer — it is simply one the teacher did not single out.
      evaluation: (mode !== 'graded' ? 'neutral' : 'incorrect') as ChipEvaluation,
      sourceId: KEYWORD_UNLISTED,
    },
  ], 'or');
}

// ─── Scale ───────────────────────────────────────────────────────────────────

function scaleValueLabelNode(
  value: number, values: { value: number; label?: string }[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): React.ReactNode {
  const inner = (values.find(v => v.value === value)?.label ?? '').replace(/<\/?p>/g, '').trim();
  if (!inner) return String(value);
  const SENTINEL = '\u0000';
  const [before = '', after = ''] = t('blocks.scale.valueWithLabel', { value, label: SENTINEL }).split(SENTINEL);
  return (
    <>
      {before}
      <PreviewHtml as='span' className='ws-formula-preview' html={inner} />
      {after}
    </>
  );
}

/**
 * Scale's two default conditions as chip lists — the ONE derivation the feedback panel and the test
 * legend both draw. The eleventh and last block to get one.
 *
 * They were two hand-rolled copies, and unlike the earlier ports the TEXT did not agree either. The
 * legend built `TestBarChip`s labelled with `scaleValueLabelNode`; the panel built its own `<span>`s
 * (literal `bg-ws-error-bg`/`max-w-32` classes, its own icon-per-role map) labelled with
 * `valueDisplayLabel`, which is an alias of the STRING derivation. That string flattens a rich label
 * through `stripHtmlForLabel`, which turns a variable into the literal text `{name}` — right for a
 * stored feedback title, wrong on screen. So a Scale position whose label held a variable rendered
 * as a real chip in the test legend and as bare `{name}` in the feedback panel one pane away,
 * against the standing rule that a `{name}` is a chip or a resolved value and never literal text.
 *
 * The catch-all is ENUMERATED, not generic. Scale's positions are a closed finite set, so the
 * complement of the correct ones is a short known list and "any other rating" would describe a
 * submission that cannot exist. Same reasoning as MCQ; the opposite of Keyword, whose domain is open.
 *
 * One chip per position, never a collapsed range: a student picks exactly one, so each is its own
 * selectable answer and a "4-5" chip would name a span nobody can choose.
 *
 * `font-mono` is preserved from the panel's copy — a position is a number read off an axis, and the
 * monospace face is what keeps it from being read as prose.
 */
export function scaleChips(
  kind: 'correct' | 'incorrect',
  reach: ScaleReachability,
  values: ScaleValue[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  /**
   * The block's grading mode, for the CATCH-ALL's list.
   *
   * Outside graded the catch-all is `any_answer` / `any_response`, and it names the same thing
   * Default Incorrect does for the same reason. Roles are not consulted there, so every chip is
   * NEUTRAL — a leftover `incorrect` from a mode the block has left must not put a ✗ in the row.
   */
  mode: GradingMode = 'graded',
): ConditionChips {
  const points = kind === 'correct' ? reach.correctValues : reach.uncoveredWrong;
  // Nothing left to name: no position is roled correct, or every non-correct one already has its own
  // targeted feedback. Both surfaces detected this before; only the panel drew a chip for it.
  if (points.length === 0) return [{ kind: 'unreachable' }];
  return joinChips(points.map(v => ({
    kind: 'chip' as const,
    // The NODE label, so a variable in a position's label is a chip on BOTH surfaces.
    label: <span className='font-mono'>{scaleValueLabelNode(v.value, values, t)}</span>,
    // The flattened string, for the plain-text fallback every element-labelled chip carries.
    text: scaleValueLabel(v.value, values, t),
    evaluation: mode !== 'graded' ? ('neutral' as const)
      : kind === 'correct' ? ('correct' as const)
      // A neutral-roled position is not WRONG, it is unscored — both copies already toned these
      // apart, and flattening that would lose a distinction the teacher authored.
      : (v.role === 'neutral' ? ('neutral' as const) : ('incorrect' as const)),
    // Lets the legend light only the position actually picked, instead of the whole row.
    sourceId: String(v.value),
  })), 'or');
}

// ─── Dropdown ────────────────────────────────────────────────────────────────

/**
 * Dropdown is a CLOSED domain, so its catch-all enumerates: the option set is finite and short, and
 * there is no "any other selection" a student could make.
 *
 * The label renderer is a PARAMETER, and deliberately: which option is passed decides how a variable
 * resolves, and that is the caller's business — the feedback panel passes the authored option so a
 * variable stays a chip, the legend passes the resolved one so it shows the rolled value.
 */
export function dropdownChips(
  kind: 'correct' | 'incorrect',
  reach: DropdownReachability,
  optLabel: (opt: DropdownOption) => React.ReactNode,
  /**
   * The block's grading mode, for the CATCH-ALL's list.
   *
   * Outside graded the catch-all is `any_answer` / `any_response`, and it names the same thing
   * Default Incorrect does for the same reason. Roles are not consulted there, so every chip is
   * NEUTRAL — a leftover `incorrect` from a mode the block has left must not put a ✗ in the row.
   */
  mode: GradingMode = 'graded',
): ConditionChips {
  const opts = kind === 'correct' ? reach.correctOptions : reach.uncoveredWrong;
  if (opts.length === 0) return [{ kind: 'unreachable' }];
  return joinChips(opts.map(opt => ({
    kind: 'chip' as const,
    label: optLabel(opt),
    text: stripHtmlForLabel(opt.text),
    evaluation: (mode !== 'graded' ? 'neutral'
      : kind === 'correct' ? 'correct'
      : opt.role === 'neutral' ? 'neutral' : 'incorrect') as ChipEvaluation,
    sourceId: opt.id,
  })), 'or');
}

// ─── FITB ────────────────────────────────────────────────────────────────────

const fitbWordLabel = fitbWordLabelText;

/** The sourceId for "a word nobody listed, in THIS blank" — scoped per blank, since each one has
 *  its own catch-all. */
export const fitbAnyOther = (blankId: string) => `${blankId}__any_other__`;

/** The chip source id for "this blank was left empty" — the editor's own trigger string, so the
 *  chip and the card it belongs to share an identity. */
export const fitbBlankEmptyTrigger = (blankId: string) => buildFitbWordTrigger(blankId, FITB_BLANK_EMPTY);

/**
 * FITB is an OPEN domain per blank, and a CONJUNCTION across them: every blank has to be right, so
 * the groups join with "and" for correct and "or" for the catch-all.
 */
export function fitbChips(
  kind: 'correct' | 'incorrect',
  byBlank: Array<{ blank: { id: string }; words: FitbBlankWord[] }>,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** See `keywordChips`' own note — FITB is the same open domain, per blank. */
  mode: GradingMode = 'graded',
  /** Triggers already claimed by a targeted state, so the row does not offer them twice. */
  covered?: ReadonlySet<string>,
): ConditionChips {
  const prefix = (blankIdx: number) => t('blocks.fitb.blankLabel', { ordinal: blankIdx + 1 });
  const groups = byBlank.map(({ blank, words }, blankIdx) => {
    const named = words.map(w => {
      const wordIdx = words.indexOf(w);
      return {
        kind: 'chip' as const,
        label: `${prefix(blankIdx)}: ${fitbWordLabel(w, wordIdx, t)}`,
        evaluation: (kind === 'correct' ? 'correct'
          : mode !== 'graded' ? 'neutral'
          : w.role === 'neutral' ? 'neutral' : 'incorrect') as ChipEvaluation,
        sourceId: w.id,
      };
    });
    if (kind === 'correct') {
      // A blank with no typeable correct word makes the whole conjunction unsatisfiable, so it says
      // so IN POSITION. Dropping the term (which the loop used to do) printed the other blank's word
      // alone and read as a condition a student could actually meet.
      return named.length > 0 ? joinChips(named, 'or')
        : [{ kind: 'unreachable' as const, label: `${prefix(blankIdx)}: ${t('blocks.feedbackPanel.unreachable')}` }];
    }
    /**
     * In OPTIONAL each blank's own "left empty" joins its row, FIRST, unless a target claims it.
     * The block-level blank does not apply to FITB — a student can leave one blank and fill
     * another — so this is the level at which the question has a single answer.
     */
    const blankEmpty = mode === 'optional' && !covered?.has(fitbBlankEmptyTrigger(blank.id))
      ? [{
          kind: 'chip' as const,
          label: `${prefix(blankIdx)}: ${t('blocks.feedbackDefaults.noResponseTitle')}`,
          evaluation: 'neutral' as ChipEvaluation,
          sourceId: fitbBlankEmptyTrigger(blank.id),
        }]
      : [];
    return joinChips([...blankEmpty, ...named, {
      kind: 'chip' as const,
      label: `${prefix(blankIdx)}: ${t('blocks.fitb.anyOtherWord')}`,
      evaluation: (mode !== 'graded' ? 'neutral' : 'incorrect') as ChipEvaluation,
      sourceId: fitbAnyOther(blank.id),
    }], 'or');
  });
  if (groups.length === 0) return [{ kind: 'unreachable' }];
  // `joinChipGroups`, shared with slider, which fans out over two levels for the same reason.
  return joinChipGroups(groups, kind === 'correct' ? 'and' : 'or');
}

// ─── Ordering ────────────────────────────────────────────────────────────────

const orderingItemLabel = orderingItemLabelText;
export const orderingCustomOrderSequence = orderingCustomOrderSequenceText;

function orderingTriggerKind(trigger: string): 'reversed' | 'adjacent_swap' | 'off_by_one' | 'custom' {
  if (trigger === 'reversed' || trigger === 'adjacent_swap' || trigger === 'off_by_one') return trigger;
  return 'custom';
}

/**
 * `orderingItemLabel` as a NODE, for the surfaces that show a condition rather than store one.
 *
 * The string version bracketizes a variable to `{name}`, which is fine for a stored title (compared
 * for collisions, embedded in plain-text sentences) and not acceptable on screen — a variable is a
 * chip or an evaluated value, never literal braces. An equation item goes through
 * AuthoredFormula; rich text goes through PreviewHtml; a media item keeps the plain
 * bracketed Label it already had, since a Label is never rich and never a variable.
 */
export function orderingItemLabelNode(
  item: OrderingItem, index: number, t: (key: string, opts?: Record<string, unknown>) => string,
  itemsAsEquations?: boolean,
): React.ReactNode {
  if (!item.media && itemsAsEquations) {
    const formula = (item.formula ?? '').trim();
    return formula
      ? <AuthoredFormula formula={formula} />
      : orderingItemLabel(item, index, t, itemsAsEquations);
  }
  /* MEDIA GETS ITS GLYPH, which this was the last place not to give it. MCQ has always drawn a
     kind icon beside the bracketed Label; Ordering bracketed and stopped there, so an image option
     and an audio option read identically in a chip. `authoredPreviewNode` is the one renderer for
     this; it returns null for a blank Label, where the positional fallback below is right. */
  if (item.media) {
    return authoredPreviewNode({ media: item.media as { kind: 'image' | 'audio' }, text: item.text })
      ?? orderingItemLabel(item, index, t, itemsAsEquations);
  }
  const inner = item.text.replace(/<\/?p>/g, '').trim();
  return inner
    // data-authored: teacher-written item text, for Preview's accessibility reports.
    ? <PreviewHtml as='span' html={inner} />
    : orderingItemLabel(item, index, t, itemsAsEquations);
}

/** A feedback state's human-readable "condition" for the live-test bar legend — same job as
 * MCQ's `mcqStateCondition`/Dropdown's `dropdownStateCondition`. Unlike those, Ordering has no
 * per-option role fan-out to worry about: `correct` is always exactly one canonical sequence,
 * and there's no discrete answer domain for `incorrect` to enumerate against, so this stays a
 * flat string in every case rather than a rich node. Reuses items already resolved against
 * rolled variable values (see computeOrderingTestData) — this is a TEST-context surface, so it
 * shows what a student would actually see this attempt, not the raw `{name}` chip the editor
 * shows while authoring. */
// `itemsAsEquations` is ONLY ever passed from the live-editing "Correct" chip (correctSlot), which
// is fed raw, unresolved editor items (real `.formula`/blank `.text` in equations mode) — every
// call from the test-data path (computeOrderingTestData's testChips/unreachable-branch condition)
// is fed items whose `.text` has ALREADY been resolved to real display content (see
// `orderingItemDisplayText`), so those calls deliberately leave this unset, same "resolved callers
// never pass it" rule as `orderingItemLabel`'s own doc comment.
export function orderingStateCondition(
  state: FeedbackState, items: OrderingItem[], t: (key: string, opts?: Record<string, unknown>) => string, itemsAsEquations?: boolean,
): React.ReactNode {
  if (state.trigger === 'unanswered') return t('blocks.feedbackDefaults.noAnswerTitle');
  if (state.trigger === 'any_answer') return t('blocks.feedbackDefaults.anyAnswerTitle');
  if (state.trigger === 'any_response') return t('blocks.feedbackDefaults.anyResponseTitle');
  // Deliberately NOT the shared cross-block `incorrectTitle` ("Incorrect") here — every other
  // targeted condition in this legend shows its actual sequence (Correct's full order, a
  // structural summary, a custom order's own sequence), so a bare "Incorrect" reads like an
  // incomplete listing next to those. Enumerating what it covers isn't practical either (the
  // remaining-permutations count explodes combinatorially with item count) — "Any Other Order" is
  // the honest description of what this catch-all actually matches.
  if (state.trigger === 'incorrect') return t('blocks.ordering.incorrectAnyOtherOrder');
  if (state.trigger === 'correct') {
    return items.map((item, i) => (
      <React.Fragment key={item.id}>{i > 0 && ', '}{orderingItemLabelNode(item, i, t, itemsAsEquations)}</React.Fragment>
    ));
  }
  const kind = orderingTriggerKind(state.trigger);
  if (kind !== 'custom') {
    const summaryKeys: Record<'reversed' | 'adjacent_swap' | 'off_by_one', string> = {
      reversed: 'triggerSummaryReversed', adjacent_swap: 'triggerSummaryAdjacentSwap', off_by_one: 'triggerSummaryOffByOne',
    };
    return t(`blocks.ordering.${summaryKeys[kind]}`);
  }
  return orderingCustomOrderSequence(state.trigger, items, t, itemsAsEquations);
}

/**
 * One state's condition as a chip list — the ONE thing the feedback panel and the test legend both
 * draw.
 *
 * `orderingStateCondition` was already shared, and the two surfaces still managed to disagree, in
 * the two ways a shared helper can still drift:
 *
 *   - The legend called it with THREE arguments. `itemsAsEquations` is optional, so an
 *     equations-mode block labelled its items one way in the panel and another in the legend, with
 *     no type error to say so.
 *   - Default Incorrect never reached it from the panel at all: the card passed
 *     `lockedTriggerLabel={incorrectAnyOtherOrderTitle}` while the legend derived the condition.
 *     Both resolve `blocks.ordering.incorrectAnyOtherOrder`, so they agreed by coincidence rather
 *     than by construction — two paths to one string, either of which could be edited alone.
 *
 * Taking the arguments once, here, removes both: a caller supplies the state and the item set, and
 * cannot supply half the context or route around the derivation.
 *
 * "Any Other Order" is deliberately NOT enumerated — six items is 720 permutations, so the
 * catch-all describes the complement rather than listing it, per the standing chip rule.
 */
export function orderingChips(
  state: FeedbackState,
  items: OrderingItem[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  itemsAsEquations: boolean | undefined,
  reachable: boolean,
  mode: GradingMode = 'graded',
  hasTargeted = false,
  surveyKey?: string,
): ConditionChips {
  if (!reachable) return [{ kind: 'unreachable' }];
  /**
   * Ordering's catch-all is the one that cannot ENUMERATE what it still catches — its answer
   * space is every permutation of the items, not a short list — so it keeps a single chip and
   * only its WORDING and its tone change. `catchAllTitle` supplies the first and returns
   * undefined wherever the block's own condition still applies, so graded is untouched.
   */
  // Keyed on the catch-all's own trigger: No Answer is a `default` too, and naming it by type
  // alone handed it the catch-all's label.
  const catchAll = state.trigger === 'any_answer' || state.trigger === 'any_response'
    ? catchAllTitle(mode, hasTargeted, t, surveyKey) : undefined;
  return [{
    kind: 'chip',
    label: catchAll ?? orderingStateCondition(state, items, t, itemsAsEquations),
    evaluation: mode !== 'graded' && hasTargeted ? 'neutral'
      : state.evaluation === 'correct' ? 'correct'
      : state.evaluation === 'neutral' ? 'neutral' : 'incorrect',
  }];
}

// ─── Sort ────────────────────────────────────────────────────────────────────

interface Binnish { id: string; label?: unknown }
interface Sortish { id: string; text?: unknown; formula?: unknown; media?: unknown }

/**
 * ONE state's condition as chips — the thing the feedback panel and the test legend both draw.
 *
 * Sort's catch-all is Ordering's problem in reverse. Ordering cannot enumerate what Correct means
 * (six items is 720 permutations), so it names the arrangement and lets the catch-all describe the
 * complement. Sort's CORRECT is perfectly enumerable — the key says which items go in which bin —
 * so it is spelled out, one chip per bin, joined by "and". It is the INCORRECT catch-all that
 * cannot be listed, and it gets the complement wording instead.
 *
 * "Correct" and "Incorrect" alone are not conditions, they are verdicts. A legend of verdicts
 * tells a teacher nothing they did not already know from the colour.
 */
/** `item:<itemId>:<binId>`, split at the LAST colon because an id may contain one — the same split
 *  `sortTargetedLabel` makes, kept here so the chip does not have to import the label module's
 *  internals. */
function parseSortItemTriggerForChip(trigger: string): { itemId: string; binId: string } | null {
  if (!trigger.startsWith('item:')) return null;
  const rest = trigger.slice('item:'.length);
  const cut = rest.lastIndexOf(':');
  if (cut <= 0) return null;
  return { itemId: rest.slice(0, cut), binId: rest.slice(cut + 1) };
}

export function sortChips(
  state: FeedbackState,
  items: Sortish[],
  bins: Binnish[],
  key: { placements: Record<string, string[]>; anyBin?: string[]; alsoAccepts?: Record<string, string[]> },
  t: (key: string, opts?: Record<string, unknown>) => string,
  reachable: boolean,
  mode: GradingMode = 'graded',
  hasTargeted = false,
  targetedLabel?: string,
  /* The RAW formula names an item here, not its value for this roll — a chip label backs a stored
     title, and a title baked from one draw disagrees with the next. Same call Ordering makes. */
  itemsAsEquations = false,
): ConditionChips {
  if (!reachable) return [{ kind: 'unreachable' }];
  const evaluation: ChipEvaluation = mode !== 'graded' && hasTargeted ? 'neutral'
    : state.evaluation === 'correct' ? 'correct'
      : state.evaluation === 'neutral' ? 'neutral' : 'incorrect';

  /* DECLARED ABOVE THE TARGETED BRANCH, which reads them. Below, they were in their temporal
     dead zone by the time a targeted chip asked for one — the same way `columns` once crashed
     the student surface from one line too far down. */
  const itemName = (id: string) => sortItemLabelText(items, id, t, itemsAsEquations);
  const binName = (id: string) => sortBinLabelText(bins, id, t);

  /**
   * THE SAME CONTENT A STUDENT SEES, in the chip.
   *
   * `sortItemLabelText` strips to plain text, which for an equation or a reaction means the raw
   * LaTeX source — a chip read `y=5x^2+5x+8y-9z+\pi` while the item two inches above it rendered
   * the formula properly. `authoredPreviewNode` injects the stored HTML instead, which already
   * carries the rendered KaTeX, and gives media its kind glyph and bracket. The plain `text`
   * channel is unchanged, because a stored title and a Flow label must stay strings.
   */
  const itemNode = (id: string) => {
    const item = items.find(i => i.id === id);
    if (!item) return itemName(id);
    const node = authoredPreviewNode({
      media: item.media as { kind: 'image' | 'audio' } | undefined,
      text: String(item.text ?? ''),
      formula: itemsAsEquations ? String(item.formula ?? '') : undefined,
    });
    return node ?? itemName(id);
  };

  /** Items of one bin, comma-separated, each rendered as its own node. */
  const itemNodes = (ids: string[]) => (
    <>
      {ids.map((id, i) => (
        <React.Fragment key={id}>
          {i > 0 ? ', ' : null}
          {itemNode(id)}
        </React.Fragment>
      ))}
    </>
  );

  const binNode = (id: string) => {
    const bin = bins.find(b => b.id === id);
    const node = bin ? authoredPreviewNode({ text: String(bin.label ?? '') }) : null;
    return node ?? binName(id);
  };

  /**
   * ONE CHIP PER NON-EMPTY BIN — the shape of an arrangement, whoever is asking.
   *
   * The correct key and a Custom Arrangement targeted state describe the same KIND of thing: every
   * item filed somewhere. Reading them off one builder is what makes the two rows comparable at a
   * glance, so a teacher can see which bin a wrong arrangement moved. Before this, a custom
   * arrangement arrived as one pre-joined string and printed raw `\pi` and reaction source beside a
   * correct chip that rendered both properly.
   *
   * `bins` orders the chips, not the trigger, so reordering the bins reorders both rows together.
   */
  const binChips = (placements: Record<string, string[]>, evaluationFor: ChipEvaluation) => bins
    .filter(bin => (placements[bin.id] ?? []).length > 0)
    .map(bin => ({
      kind: 'chip' as const,
      sourceId: bin.id,
      /* TWO CHANNELS. `label` is what the panel and the legend DRAW, and it carries nodes so an
         equation renders; `text` is the plain string a stored title and Flow read. `<Trans>` is
         what lets a node sit inside a translated sentence — the connective and the word order
         belong to the translation, and splitting the template in code would hard-code English. */
      label: (
        <Trans
          i18nKey='blocks.sort.chipItemsInBinRich'
          components={{
            items: <>{itemNodes(placements[bin.id] ?? [])}</>,
            bin: <>{binNode(bin.id)}</>,
          }}
        />
      ),
      text: t('blocks.sort.chipItemsInBin', {
        items: (placements[bin.id] ?? []).map(itemName).join(', '),
        bin: binName(bin.id),
      }),
      evaluation: evaluationFor,
    }));

  if (state.type === 'targeted') {
    /* The trigger already reads as its own sentence — "Trilobite in Paleozoic" — derived once by
       the block and passed in, so the panel and the legend cannot word it differently.
       An ITEM trigger is the one that embeds authored content, so it is redrawn with nodes while
       the passed string stays as the plain `text`. The structural triggers are fixed labels with
       nothing to render, and the ORDER triggers embed only a bin name — worth the same treatment
       if a teacher ever puts an equation in a bin label, and not yet done. */
    /* A CUSTOM ARRANGEMENT is a whole arrangement, so it reads as one, through the same builder
       the correct key uses. The × already says it is wrong; a "Custom Arrangement:" prefix on top
       of that only costs the row the width it needs to show the arrangement itself. */
    const partition = parseSortPartitionTrigger(state.trigger);
    if (partition) {
      const chips = binChips(partition, evaluation);
      return chips.length ? joinChips(chips, 'and') : [{ kind: 'chip', label: targetedLabel ?? '', text: targetedLabel ?? '', evaluation }];
    }
    const parsed = parseSortItemTriggerForChip(state.trigger);
    const richTargeted = parsed && items.some(i => i.id === parsed.itemId) ? (
      <Trans
        i18nKey='blocks.sort.chipItemInBinRich'
        components={{ item: <>{itemNode(parsed.itemId)}</>, bin: <>{binNode(parsed.binId)}</> }}
      />
    ) : null;
    return [{
      kind: 'chip',
      label: richTargeted ?? targetedLabel ?? '',
      text: targetedLabel ?? '',
      evaluation,
    }];
  }

  /* THE SHARED KEYS, as every block uses. These two states mean the same thing wherever they
     appear — nothing answered, something half answered — so a block wording them for itself is a
     block saying a universal thing in a private voice. `orderingStateCondition` reaches for the
     same two, and so does the test bar's own category heading. */
  if (state.trigger === 'unanswered') {
    return [{ kind: 'chip', label: t('blocks.feedbackDefaults.noAnswerTitle'), evaluation }];
  }
  if (state.trigger === 'incomplete_answer') {
    return [{ kind: 'chip', label: t('blocks.feedbackDefaults.incompleteAnswerTitle'), evaluation }];
  }

  const catchAll = catchAllTitle(mode, hasTargeted, t, 'blocks.sort.chipAnyOther');
  if (catchAll) return [{ kind: 'chip', label: catchAll, evaluation }];

  if (state.trigger === 'incorrect') {
    /* NOT enumerated: the wrong arrangements of n items over k bins are k^n minus the right ones,
       which no legend can carry. The catch-all describes the complement, per the standing rule. */
    return [{ kind: 'chip', label: t('blocks.sort.chipAnyOther'), evaluation }];
  }

  /* Correct: one chip per bin that has anything in it, which IS the answer key. The per-bin text
     comes from `sortItemLabelText`/`sortBinLabelText`, the same two functions Flow's own label
     calls — so the chip, the title and Flow cannot word one bin differently. */
  const placed = binChips(key.placements, evaluation);
  /* An item the key never placed means no arrangement can be correct — the evaluator's own
     `keyIsComplete` guard. Saying so here beats printing a partial key that cannot ever fire. */
  if (placed.length === 0 || items.some(i => !Object.values(key.placements).flat().includes(i.id))) {
    return [{ kind: 'unreachable' }];
  }
  return joinChips(placed, 'and');
}

// ─── GPS ─────────────────────────────────────────────────────────────────────

/** A target with a valid coordinate AND a positive radius. A radius alone describes a circle around
 *  nowhere, which is what once printed "Within 10 m" for a block with no target set. */
export function isGpsSetupIncomplete(d: GPSEditorData): boolean {
  const lat = parseFloat(d.targetLat), lng = parseFloat(d.targetLng);
  const tol = parseFloat(d.toleranceRadius);
  return !isValidGpsCoord(lat, lng) || !(Number.isFinite(tol) && tol > 0);
}

function isValidGpsCoord(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}


/**
 * GPS's two default conditions as chip lists — the last of the eleven blocks to get one.
 *
 * Thin on purpose, and worth saying why. The other ten needed this to collapse two hand-rolled
 * copies; GPS never had two. Both surfaces already read `gpsCorrectChipLabel` /
 * `gpsIncorrectChipLabel`, and its conditions are single sentences ("Within 25 m of 40.71280,
 * -74.00600") with no fan-out, no per-option identity and no rich text — so there is no `joinChips`
 * here and never will be.
 *
 * What it DOES buy is the two things the surfaces disagreed on:
 *
 *  1. A null label on a REACHABLE state. Both label functions return null when the tolerance radius
 *     is not a positive number, which a teacher produces just by clearing the field. The panel
 *     already fell back to the per-gap breakdown; the legend passed the null straight through as a
 *     chip `condition`, and a null condition renders as a bare icon with no text beside the category
 *     heading. That exact bug was found and fixed once before on this block's gate chips; this is
 *     the same fault in the Default Incorrect chip. Handled once, here.
 *  2. Unreachability. The panel drew its own `UnreachableChip` while the legend passed the
 *     translated string as ordinary chip text, so the two rendered the same fact differently.
 *     `{ kind: 'unreachable' }` lets each renderer draw it its own correct way.
 */
/**
 * GPS's chips, toned for the mode they are shown in — see `compassChips` for the shared note.
 */
export function gpsChips(
  kind: 'correct' | 'incorrect',
  d: GPSEditorData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  mode: GradingMode,
  acceptedClaimed = true,
): ConditionChips {
  const chips = gpsChipsIn(kind, d, t, locale, acceptedClaimed);
  return mode === 'graded' ? chips : neutralChips(chips);
}

function gpsChipsIn(
  kind: 'correct' | 'incorrect',
  d: GPSEditorData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  /**
   * Whether the accepted band is SPOKEN FOR, and so carved out of what the catch-all still gets.
   *
   * It always is in graded, where Correct is checked first and is terminal. Outside graded it is
   * only spoken for when a Set Answer target claims it — and when nothing does, subtracting it
   * describes a hole that is not there: the catch-all really does catch readings inside the band,
   * because nothing else is looking at them.
   */
  acceptedClaimed = true,
): ConditionChips {
  const tol = parseFloat(d.toleranceRadius);
  const acceptedRange: GPSInterval | null = acceptedClaimed && Number.isFinite(tol) && tol > 0
    ? { lo: 0, hi: tol } : null;

  if (kind === 'correct') {
    // A target coordinate AND a usable radius. A radius alone describes a circle around nowhere,
    // which is what once printed "Within 10 m" off the default for a block with no target set.
    if (isGpsSetupIncomplete(d)) return [{ kind: 'unreachable' }];
    const label = gpsCorrectChipLabel(d, t, locale);
    return label ? [{ kind: 'chip', label, evaluation: 'correct' }] : [{ kind: 'unreachable' }];
  }

  // `?? []` because a block being added has no feedback list yet, and this runs on first render.
  const triggers = (d.feedback?.states ?? []).filter(st => st.type === 'targeted').map(st => st.trigger);
  if (isGpsDefaultIncorrectUnreachable(acceptedRange, triggers)) return [{ kind: 'unreachable' }];
  /*
   * The GAPS decide, and `gpsIncorrectChipLabel` is only allowed to speak for the one gap it can
   * describe.
   *
   * It says "More than {radius} from {target}" off the radius alone — a sentence that is true only
   * while nothing else has carved the line up. It used to win the `??` outright, so a block with
   * targets at "less than 10" and "20 to 40" still printed "More than 10", hiding both leftovers
   * (10 to 20, and beyond 40). Take the sentence when the gap set IS exactly "everything past the
   * radius" — it reads better and names the origin — and enumerate otherwise.
   */
  const gaps = computeGpsDefaultIncorrectGaps(acceptedRange, triggers);
  const onlyBeyondRadius = gaps.length === 1 && acceptedRange !== null
    && gaps[0]!.lo === acceptedRange.hi && gaps[0]!.hi === Infinity;
  const sentence = onlyBeyondRadius ? gpsIncorrectChipLabel(d, t, locale) : null;
  if (sentence) return [{ kind: 'chip', label: sentence, evaluation: 'incorrect' }];
  // One chip per leftover band, joined like every other fan-out, rather than one chip holding a
  // sentence with "or" inside it: the surfaces draw the connector themselves.
  return joinChips(
    gaps.map(g => ({ kind: 'chip' as const, label: gpsGapChipLabel(g, d, t, locale), evaluation: 'incorrect' as const })),
    'or');
}

// ─── Compass ─────────────────────────────────────────────────────────────────

const COMPASS_MAX_TOLERANCE = 180;

function num(s: string): number {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

/** Wraps a raw degree value into [0, 360) for display only: a tolerance band of 360° ± 2° reads as
 *  "358° to 2°" (lo wraps below 0, hi normalizes above 360). */
function normalizeDegreesForDisplay(deg: number): number {
  const m = deg % 360;
  return m < 0 ? m + 360 : m;
}

// English abbreviations differ from Portuguese ones (Leste/Oeste/Sudoeste/Noroeste, not
// East/West/Southwest/Northwest) — never hardcode the letters, always go through i18n.
const CARDINAL_I18N_KEYS: Record<CompassPoint, string> = {
  N: 'cardinalN', NE: 'cardinalNE', E: 'cardinalE', SE: 'cardinalSE',
  S: 'cardinalS', SW: 'cardinalSW', W: 'cardinalW', NW: 'cardinalNW',
};

/**
 * An open end of a band, marked with the shared comparison symbol.
 *
 * The degree blocks say their one-sided conditions in symbols already — angle's own triggers draw
 * "< 40°" and "≥ 40°" — so a gap that stops just short of a bound says so the same way rather
 * than growing a second vocabulary of words. GPS reads in prose because its conditions always
 * did ("Within 25 m", "More than 25 m"); each block keeps the voice it already had.
 *
 * `CUT_SYMBOLS` is the one table, and an invariant stops anyone re-spelling it.
 */
function openEnd(text: string, inclusive: boolean, side: 'lo' | 'hi'): string {
  return inclusive ? text : `${cutSymbol(side === 'lo' ? 'gt' : 'lt')} ${text}`;
}

export function formatCardinalRange(
  lo: number, hi: number,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  /** Which ends the band owns. Closed by default: every authored compass condition is. */
  loInclusive = true, hiInclusive = true,
): string {
  const loDisp = normalizeDegreesForDisplay(lo);
  const hiDisp = normalizeDegreesForDisplay(hi);
  const loCardinal = t(`blocks.compass.${CARDINAL_I18N_KEYS[cardinalPointFromDegrees(lo)]}`);
  // Same raw-difference check as CompassArcDiagram: `% 360` collapses a full turn to zero, which
  // would print "N (0°)" — a single point — for a band that in fact accepts every heading.
  if (Math.abs(hi - lo) >= 360 - 1e-9) return t('blocks.compass.chipFullCircle');
  const span = ((hi - lo) % 360 + 360) % 360;
  if (span < 1e-9) return t('blocks.compass.chipPoint', { cardinal: loCardinal, deg: fmtLocale(loDisp, locale) });
  const hiCardinal = t(`blocks.compass.${CARDINAL_I18N_KEYS[cardinalPointFromDegrees(hi)]}`);
  return t('blocks.compass.chipRange', {
    loCardinal: openEnd(loCardinal, loInclusive, 'lo'),
    loDeg: fmtLocale(loDisp, locale),
    hiCardinal: openEnd(hiCardinal, hiInclusive, 'hi'),
    hiDeg: fmtLocale(hiDisp, locale),
  });
}

export function correctRangePairs(d: CompassEditorData, resolve: (s: string, fieldId?: string) => number = num): { lo: number; hi: number }[] {
  if (d.conditionType === 'exact') {
    return d.answers.filter(a => a.value.trim() !== '').map(a => {
      const v = resolve(a.value);
      // Clamped independently of the validation issue above: a teacher mid-keystroke is briefly at
      // an over-large tolerance on the way to a valid one, and the arc must not try to draw a
      // multi-turn wedge in the meantime. At the cap the wedge is exactly one full circle, which is
      // the honest picture of "every heading accepted".
      const cap = (n: number) => Math.min(Math.max(n, 0), COMPASS_MAX_TOLERANCE);
      const asym = answerSymmetry(a, d) === 'asymmetric';
      const loErr = cap(asym ? num(a.toleranceBelow) : num(a.toleranceValue));
      const hiErr = cap(asym ? num(a.toleranceAbove) : num(a.toleranceValue));
      return { lo: v - loErr, hi: v + hiErr };
    });
  }
  // Blank and half-filled rows dropped — see `compassAcceptedRangeConfig`'s note. `num('')` is 0,
  // so without this a low of 23 with a blank high drew a wedge from 23 back round to due north.
  return d.betweenAnswers
    .filter(a => a.low.trim() !== '' && a.high.trim() !== '')
    .map(a => ({ lo: resolve(a.low), hi: resolve(a.high) }));
}

export function compassAcceptedRangeConfig(
  d: CompassEditorData,
  /** `fieldId` so each answer resolves in ITS OWN angle mode. The ids are right here, and a
   *  resolver that ignored them would read every field in one mode. */
  resolve: (s: string, fieldId: string) => number = num,
) {
  /*
   * BLANK rows are dropped before anything is computed from them.
   *
   * The default resolver is `num`, and `num('')` is 0 — a perfectly resolvable number — so an
   * untouched answer row became a real accepted band at zero, and a `between` row with one bound
   * filled became an INVERTED band: "23 to 0" on screen for an authored low of 23 and a blank high.
   * The evaluator's own unresolvable-answer filter cannot catch either, for the same reason.
   *
   * Dropping them is also what makes the answer bar honest: with no band left, the reading is
   * "No Valid Answer" (the row IS authored, it just admits nothing) instead of a fabricated range.
   */
  const authored = (v: string) => v.trim() !== '';
  return {
    conditionType: d.conditionType,
    answers: d.answers.filter(a => authored(a.value))
      .map(a => ({ id: a.id, value: resolve(a.value, `${a.id}.value`), toleranceValue: num(a.toleranceValue), toleranceAbove: num(a.toleranceAbove), toleranceBelow: num(a.toleranceBelow) })),
    // BOTH bounds: a half-filled row is dropped by the evaluator too, and
    // `incompleteBetweenRowIds` is what tells the teacher about it.
    betweenAnswers: d.betweenAnswers.filter(a => authored(a.low) && authored(a.high))
      .map(a => ({ id: a.id, low: resolve(a.low, a.id), high: resolve(a.high, a.id) })),
    toleranceSymmetry: d.toleranceSymmetry,
  };
}

/**
 * THE MODE REACHES THE DERIVATION, the way it does for every other block's chips.
 *
 * These two were the only chip functions taking no `mode`. They colour from a correctness notion
 * — GPS from a `kind` argument, Compass from `state.evaluation` — which is honest in graded and
 * meaningless in a mode with nothing to be wrong about, so outside graded they emitted red.
 *
 * The decision was pushed out to roughly SEVENTEEN call sites instead, and they disagreed: some
 * wrapped in `neutralChips` by hand, one wrote `gradingMode === 'graded' ? … : …` inline, and
 * several passed the chips through raw. The central force in `catchAllChips` only ever covered
 * the catch-all path; every other surface was an open leak.
 *
 * `mode` is REQUIRED rather than defaulted on purpose. A default would leave seventeen places
 * one argument away from the old behaviour and no way to find the one that forgot — the same
 * reason `ScenarioLeaderVersion.name` is required-but-nullable.
 *
 * `catchAllChips` still re-tones on top of this, and the layering is deliberate: this says "a
 * correctness colour is meaningless here", that says "these particular answers satisfy the
 * block". The second wins where it applies, which is the catch-all alone.
 */
export function compassChips(
  d: CompassEditorData,
  state: { trigger: string; evaluation?: string },
  roll: ChipRoll,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  mode: GradingMode,
  targetedTriggers: string[] = [],
  acceptedClaimed = true,
): ConditionChips {
  const chips = compassChipsIn(d, state, roll, t, locale, targetedTriggers, acceptedClaimed);
  return mode === 'graded' ? chips : neutralChips(chips);
}

function compassChipsIn(
  d: CompassEditorData,
  state: { trigger: string; evaluation?: string },
  roll: ChipRoll,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  targetedTriggers: string[] = [],
  /**
   * Whether the accepted band is SPOKEN FOR, and so carved out of what the catch-all still gets.
   *
   * Always true in graded, where Correct is checked first and is terminal. Outside graded only a
   * Set Answer target claims it — and when nothing does, subtracting it describes a hole that is
   * not there: the catch-all really does catch headings inside the band, because nothing else is
   * looking at them.
   */
  acceptedClaimed = true,
): ConditionChips {
  const exprs = answerExprTexts(d);
  const varying = exprs.some(hasVariableChip);
  const rangeChips = (rs: { lo: number; hi: number; loInclusive?: boolean; hiInclusive?: boolean }[], evaluation: 'correct' | 'incorrect') => {
    const usable = rs.filter(r => Number.isFinite(r.lo) && Number.isFinite(r.hi));
    if (usable.length === 0) return [];
    // "or" between bands: a heading lands in exactly one of them.
    return joinChips(usable.map(r => ({
      kind: 'chip' as const, evaluation,
      label: formatCardinalRange(r.lo, r.hi, t, locale, r.loInclusive ?? true, r.hiInclusive ?? true),
      // The band's own identity, so the legend lights only the one a reading landed in. Without it
      // `toTestBarChips` applies `active` state-wide and the whole row lit at once.
      sourceId: intervalSourceId('', r.lo, r.hi),
    })), 'or');
  };

  if (varying && roll === null) {
    if (exprs.length === 0) return [];
    if (state.trigger === 'correct') {
      return joinChips(exprs.map(e => ({
        kind: 'chip' as const, label: '', formulas: [e], text: e, evaluation: 'correct' as const,
      })), 'or');
    }
    return joinChips(exprs.map(e => ({
      kind: 'chip' as const, label: `${t('blocks.answers.notWord')} `, formulas: [e],
      text: `${t('blocks.answers.notWord')} ${e}`, evaluation: 'incorrect' as const,
    })), 'and');
  }

  // Each field in its own mode: these configs resolve a block's answers, and an answer is a
  // formula field. `fieldId` is optional so the shared range helpers can call it either way.
  const resolve = roll
    ? (v: string, fieldId = '') => evaluateFormulaRaw(v, roll, angleModeFor(d, fieldId))
    : (v: string) => num(v);
  /*
   * Correct uses `correctRangePairs` — the PRE-wraparound-split pairs, one per authored answer.
   * `computeCompassAcceptedRanges` splits a band that crosses 0/360 into two intervals, which the
   * gap maths needs and a chip must not show: an answer of 350 +/- 20 is ONE answer and belongs in
   * ONE chip reading "350 to 10", not two chips implying the teacher authored two bands. That
   * distinction is why `correctRangePairs` exists (see its own note), and using the split version
   * here would have silently doubled every wrapped answer's chips.
   */
  if (state.trigger === 'correct') return rangeChips(correctRangePairs(d, resolve), 'correct');
  const accepted = acceptedClaimed
    ? computeCompassAcceptedRanges(compassAcceptedRangeConfig(d, resolve)) : [];
  const gaps = computeCompassDefaultIncorrectGaps(accepted, targetedTriggers);
  const chips = rangeChips(gaps, 'incorrect');
  return chips.length === 0 ? [{ kind: 'unreachable' }] : chips;
}

// ─── Angle ───────────────────────────────────────────────────────────────────

export function rotationAcceptedRangeConfig(
  d: RotationEditorData,
  /** `fieldId` so each answer resolves in ITS OWN angle mode. The ids are right here, and a
   *  resolver that ignored them would read every field in one mode. */
  resolve: (s: string, fieldId: string) => number = num,
) {
  /*
   * BLANK rows are dropped before anything is computed from them.
   *
   * The default resolver is `num`, and `num('')` is 0 — a perfectly resolvable number — so an
   * untouched answer row became a real accepted band at zero, and a `between` row with one bound
   * filled became an INVERTED band: "23 to 0" on screen for an authored low of 23 and a blank high.
   * The evaluator's own unresolvable-answer filter cannot catch either, for the same reason.
   *
   * Dropping them is also what makes the answer bar honest: with no band left, the reading is
   * "No Valid Answer" (the row IS authored, it just admits nothing) instead of a fabricated range.
   */
  const authored = (v: string) => v.trim() !== '';
  return {
    conditionType: d.conditionType,
    answers: d.answers.filter(a => authored(a.value))
      .map(a => ({ id: a.id, value: resolve(a.value, `${a.id}.value`), toleranceValue: num(a.toleranceValue), toleranceAbove: num(a.toleranceAbove), toleranceBelow: num(a.toleranceBelow) })),
    // BOTH bounds: a half-filled row is dropped by the evaluator too, and
    // `incompleteBetweenRowIds` is what tells the teacher about it.
    betweenAnswers: d.betweenAnswers.filter(a => authored(a.low) && authored(a.high))
      .map(a => ({ id: a.id, low: resolve(a.low, a.id), high: resolve(a.high, a.id) })),
    toleranceSymmetry: d.toleranceSymmetry,
    threshold: resolve(d.threshold, 'threshold'),
  };
}

// ─── Standardized issue reporting ──────────────────────────────────────────────
// Same shape and rules as getGpsIssues/getCompassIssues — see gps-block.tsx's header comment.
//
// Angle-specific: unlike Compass, a between-range whose low exceeds its high IS an error here,
// not a wraparound. Angle's domain is the closed [0, 360] loop with no seam-crossing wedge
// concept, so there's no reading under which 200-to-30 is a deliberate answer.

export function formatRotationRange(
  lo: number, hi: number,
  t: (k: string, o?: Record<string, unknown>) => string,
  locale: string,
  /** Which ends the band owns. Closed by default: an authored `range` is closed at both. */
  loInclusive = true, hiInclusive = true,
): string {
  if (Math.abs(hi - lo) >= 360 - 1e-9) return t('blocks.rotation.chipAnyValue');
  // A band with no width is a POINT, and printing it as a range says the same number twice: an
  // exact answer of 51 with no tolerance read "51° to 51°". Compass has had this branch all along
  // (`span < 1e-9` → `chipPoint`); angle simply never grew one.
  if (Math.abs(hi - lo) < 1e-9) return t('blocks.rotation.chipPoint', { deg: fmtDegrees(lo, locale) });
  return t('blocks.rotation.gapRange', {
    min: openEnd(fmtDegrees(lo, locale), loInclusive, 'lo'),
    max: openEnd(fmtDegrees(hi, locale), hiInclusive, 'hi'),
  });
}

export function rotationChips(
  d: RotationEditorData,
  state: { trigger: string },
  roll: ChipRoll,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  targetedTriggers: string[] = [],
): ConditionChips {
  const exprs = answerExprTexts(d);
  const varying = exprs.some(hasVariableChip);
  const rangeChips = (rs: (RotationInterval & { loInclusive?: boolean; hiInclusive?: boolean })[], evaluation: 'correct' | 'incorrect') => {
    const usable = rs.filter(r => Number.isFinite(r.lo) && Number.isFinite(r.hi));
    if (usable.length === 0) return [];
    // "or" between bands: a reading lands in exactly one of them.
    return joinChips(usable.map(r => ({
      kind: 'chip' as const, evaluation,
      label: formatRotationRange(r.lo, r.hi, t, locale, r.loInclusive ?? true, r.hiInclusive ?? true),
      // The band's own identity, so the legend lights only the one a reading landed in. Without it
      // `toTestBarChips` applies `active` state-wide and the whole row lit at once.
      sourceId: intervalSourceId('', r.lo, r.hi),
    })), 'or');
  };

  if (varying && roll === null) {
    if (exprs.length === 0) return [];
    const symbol = cutSymbol(d.conditionType);
    const withSym = (e: string) => symbol ? `${symbol} ${e}` : e;
    if (state.trigger === 'correct') {
      return joinChips(exprs.map(e => ({
        kind: 'chip' as const, label: symbol ? `${symbol} ` : '', formulas: [e],
        text: withSym(e), evaluation: 'correct' as const,
      })), 'or');
    }
    return joinChips(exprs.map(e => ({
      kind: 'chip' as const, label: `${t('blocks.answers.notWord')} ${symbol ? `${symbol} ` : ''}`,
      formulas: [e], text: `${t('blocks.answers.notWord')} ${withSym(e)}`,
      evaluation: 'incorrect' as const,
    })), 'and');
  }

  // Each field in its own mode: these configs resolve a block's answers, and an answer is a
  // formula field. `fieldId` is optional so the shared range helpers can call it either way.
  const resolve = roll
    ? (v: string, fieldId = '') => evaluateFormulaRaw(v, roll, angleModeFor(d, fieldId))
    : (v: string) => num(v);
  const accepted = computeRotationAcceptedRanges(rotationAcceptedRangeConfig(d, resolve));

  // A one-sided condition states its COMPARISON, never the span it clamps to — see `cutChip`,
  // which is shared precisely because three blocks each got this differently wrong. Angle's own
  // formula path a few lines above has always rendered these as "> {x}".
  if (state.trigger === 'correct' && isCutCondition(d.conditionType)) {
    return cutChip(d.conditionType, String(d.threshold ?? ''), resolve(String(d.threshold ?? '')),
      // `chipPoint` is already "one angle, with its degree symbol", so the unit cannot drift away
      // from every other angle chip.
      v => t('blocks.rotation.chipPoint', { deg: fmtDegrees(v, locale) })) ?? [];
  }

  if (state.trigger === 'correct') return rangeChips(accepted, 'correct');
  const gaps = computeRotationDefaultIncorrectGaps(accepted, targetedTriggers);
  const chips = rangeChips(gaps, 'incorrect');
  return chips.length === 0 ? [{ kind: 'unreachable' }] : chips;
}

// ─── Tilt ────────────────────────────────────────────────────────────────────

export function tiltAcceptedRangeConfig(
  d: TiltEditorData,
  /** `fieldId` so each answer resolves in ITS OWN angle mode. The ids are right here, and a
   *  resolver that ignored them would read every field in one mode. */
  resolve: (s: string, fieldId: string) => number = num,
) {
  // Blank rows are dropped before anything is computed from them — same reasoning as Rotation's,
  // where `num('')` is 0 and an untouched row became a real accepted band at zero.
  const authored = (v: string) => v.trim() !== '';
  return {
    conditionType: d.conditionType,
    answers: d.answers.filter(a => authored(a.value))
      .map(a => ({ id: a.id, value: resolve(a.value, `${a.id}.value`), toleranceValue: num(a.toleranceValue), toleranceAbove: num(a.toleranceAbove), toleranceBelow: num(a.toleranceBelow) })),
    betweenAnswers: d.betweenAnswers.filter(a => authored(a.low) && authored(a.high))
      .map(a => ({ id: a.id, low: resolve(a.low, a.id), high: resolve(a.high, a.id) })),
    toleranceSymmetry: d.toleranceSymmetry,
    threshold: resolve(d.threshold, 'threshold'),
  };
}

/**
 * One band, named. "Everything" is the whole 0-180 span, which is the only band that can honestly
 * be called any tilt at all.
 */
export function formatTiltRange(
  lo: number, hi: number,
  t: (k: string, o?: Record<string, unknown>) => string,
  locale: string,
  loInclusive = true, hiInclusive = true,
): string {
  if (hi - lo >= (TILT_DOMAIN_HI - TILT_DOMAIN_LO) - 1e-9) return t('blocks.tilt.chipAnyValue');
  // A band with no width is a POINT; printing it as a range says the same number twice.
  if (Math.abs(hi - lo) < 1e-9) return t('blocks.tilt.chipPoint', { deg: fmtDegrees(lo, locale) });
  return t('blocks.tilt.gapRange', {
    min: openEnd(fmtDegrees(lo, locale), loInclusive, 'lo'),
    max: openEnd(fmtDegrees(hi, locale), hiInclusive, 'hi'),
  });
}

export function tiltChips(
  d: TiltEditorData,
  state: { trigger: string },
  roll: ChipRoll,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  targetedTriggers: string[] = [],
  /**
   * Whether the accepted band is SPOKEN FOR, and so carved out of what the catch-all still gets.
   *
   * Always true in graded, where Correct is checked first and is terminal. Outside graded only a
   * Set Answer target claims it — and when nothing does, subtracting it describes a hole that is
   * not there: the catch-all really does catch tilts inside the band, because nothing else is
   * looking at them. Same rule, same words, as Compass's and GPS's.
   */
  acceptedClaimed = true,
): ConditionChips {
  const exprs = answerExprTexts(d);
  const varying = exprs.some(hasVariableChip);
  const rangeChips = (rs: (TiltInterval & { loInclusive?: boolean; hiInclusive?: boolean })[], evaluation: 'correct' | 'incorrect') => {
    const usable = rs.filter(r => Number.isFinite(r.lo) && Number.isFinite(r.hi));
    if (usable.length === 0) return [];
    return joinChips(usable.map(r => ({
      kind: 'chip' as const, evaluation,
      label: formatTiltRange(r.lo, r.hi, t, locale, r.loInclusive ?? true, r.hiInclusive ?? true),
      sourceId: intervalSourceId('', r.lo, r.hi),
    })), 'or');
  };

  if (varying && roll === null) {
    if (exprs.length === 0) return [];
    const symbol = cutSymbol(d.conditionType);
    const withSym = (e: string) => symbol ? `${symbol} ${e}` : e;
    if (state.trigger === 'correct') {
      return joinChips(exprs.map(e => ({
        kind: 'chip' as const, label: symbol ? `${symbol} ` : '', formulas: [e],
        text: withSym(e), evaluation: 'correct' as const,
      })), 'or');
    }
    return joinChips(exprs.map(e => ({
      kind: 'chip' as const, label: `${t('blocks.answers.notWord')} ${symbol ? `${symbol} ` : ''}`,
      formulas: [e], text: `${t('blocks.answers.notWord')} ${withSym(e)}`,
      evaluation: 'incorrect' as const,
    })), 'and');
  }

  // Each field in its own mode: these configs resolve a block's answers, and an answer is a
  // formula field. `fieldId` is optional so the shared range helpers can call it either way.
  const resolve = roll
    ? (v: string, fieldId = '') => evaluateFormulaRaw(v, roll, angleModeFor(d, fieldId))
    : (v: string) => num(v);
  const accepted = computeTiltAcceptedRanges(tiltAcceptedRangeConfig(d, resolve));

  // A one-sided condition states its COMPARISON, never the span it clamps to — see `cutChip`.
  if (state.trigger === 'correct' && isCutCondition(d.conditionType)) {
    return cutChip(d.conditionType, String(d.threshold ?? ''), resolve(String(d.threshold ?? '')),
      v => t('blocks.tilt.chipPoint', { deg: fmtDegrees(v, locale) })) ?? [];
  }

  if (state.trigger === 'correct') return rangeChips(accepted, 'correct');
  const gaps = computeTiltDefaultIncorrectGaps(acceptedClaimed ? accepted : [], targetedTriggers);
  const chips = rangeChips(gaps, 'incorrect');
  return chips.length === 0 ? [{ kind: 'unreachable' }] : chips;
}

// ─── MCQ ─────────────────────────────────────────────────────────────────────

/**
 * A FORMULA-MODE ITEM, drawn against the ambient roll.
 *
 * `FormulaWithValues` resolves a `{name}` or an `[ara_…]` only when it is handed values, and every
 * chip site passed it `{}` — so an equations-mode item printed its chips unresolved on a test
 * surface too. The roll comes from the context rather than a parameter because the chip builders
 * are plain functions called from a dozen places, none of which have it; `useBlockPreview` returns
 * `null` on the authoring canvas, which is display mode and exactly where a chip should stay a
 * chip.
 */
function AuthoredFormula({ formula }: { formula: string }) {
  const preview = useBlockPreview();
  return <FormulaWithValues formula={formula} rolledValues={preview ?? {}} />;
}

/**
 * THE ONE WAY A CHIP OR A MENU DRAWS AUTHORED CONTENT.
 *
 * Three jobs, which is why every block wants it and why they drifted:
 *
 * - **Media** gets a KIND GLYPH and a bracketed Label. The bracket says "this is the teacher's
 *   internal filing name, not what a student reads"; the glyph says which medium, which a bracket
 *   cannot. Plain text deliberately, never `dangerouslySetInnerHTML` — the Label is a plain input.
 * - **Authored rich text** goes through `PreviewHtml`, which injects it AS STORED — which is what
 *   makes equations and reactions render — and applies the three-mode rule to the chips inside it:
 *   a variable or an answer reference stays a chip on the authoring canvas and resolves against
 *   the roll on a test surface. `rich-text-editor` post-processes its own output through `processMathHTML` /
 *   `processReactionHTML` before storing, so a stored `[data-math-latex]` span already CONTAINS
 *   rendered KaTeX. Anything that injects it gets the rendered formula for free — and anything
 *   that runs `stripHtmlForLabel` throws it away and substitutes the raw LaTeX source, which is
 *   exactly how Sort's legend came to read `y=5x^2+...\pi`.
 * - **A formula-mode item** carries a plain expression rather than HTML, so it is set in mono like
 *   Keyword's words rather than injected.
 *
 * Takes the shape rather than a block's option type, because MCQ options, Ordering items and Sort
 * items all have it. `mcqOptionPreviewNode` stays as its original name for MCQ's three call sites.
 */
export function authoredPreviewNode(o: {
  media?: { kind: 'image' | 'audio' };
  text: string;
  /** Set only in a formula mode, where the content is an expression and not rich text. */
  formula?: string;
}): React.ReactNode {
  if (o.formula !== undefined && !o.media) {
    const f = o.formula.trim();
    /* `AuthoredFormula`, not mono plain text: a formula's `{name}` printed as literal braces,
       which no surface may show, and Ordering's own node already used FormulaWithValues here. */
    return f ? <AuthoredFormula formula={f} /> : null;
  }
  return mcqOptionPreviewNode(o as MCQOption);
}

export function mcqOptionPreviewNode(opt: { media?: { kind: 'image' | 'audio' }; text: string }): React.ReactNode {
  if (opt.media) {
    // The Label is a plain input (see SortableOptionRow) — rendered as plain text, never
    // dangerouslySetInnerHTML, which also sidesteps any leftover HTML from before that fix.
    const label = stripHtmlForLabel(opt.text);
    if (!label) return null;
    return (
      /* `inline-flex`, not `flex`. A flex span is a BLOCK box, so a media chip broke the line after
         it — "[flying squirrel]," then "2, 3, 4" on the next row. It sits in flowing text beside
         its siblings, so it has to be inline. */
      <span className='inline-flex min-w-0 items-center gap-1 align-middle'>
        <MediaKindIcon kind={opt.media.kind} className='h-3 w-3 shrink-0' />
        <span className='ws-formula-preview'>[{label}]</span>
      </span>
    );
  }
  const inner = opt.text.replace(/<\/?p>/g, '').trim();
  if (!inner) return null;
  /* `PreviewHtml`, not a raw injection: it is the ONE renderer that reads the three-mode rule —
     an answer reference and a variable chip stay chips on the authoring canvas and resolve on a
     test surface — and it remaps stored palette hexes to theme tokens on the way. A chip that
     injected the stored html itself got the rendered KaTeX but never the roll, so an item reading
     `{river}` in the student's hands read `{river}` in the legend mid-test. */
  return <PreviewHtml as='span' className='ws-formula-preview' html={inner} />;
}

export function mcqChips(
  kind: 'correct' | 'incorrect',
  reach: MCQReachability,
  allowMultiple: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /**
   * The block's grading mode, for the CATCH-ALL's list.
   *
   * Outside graded the catch-all is `any_answer` / `any_response` rather than Default Incorrect,
   * and it lists the same thing for the same reason: MCQ's option set is CLOSED, so what still
   * falls through is a short known list and a teacher can read it. Enumerating is this block's
   * own Default Incorrect behaviour — the blocks whose answer space is open say "any other X"
   * instead, and keep doing so.
   */
  mode: GradingMode = 'graded',
): ConditionChips {
  const chip = (opt: MCQOption, evaluation: ChipEvaluation, extra?: Partial<{ slashed: boolean; muted: boolean; tag: string }>) => ({
    kind: 'chip' as const,
    label: mcqOptionPreviewNode(opt),
    text: stripHtmlForLabel(opt.text),
    evaluation,
    sourceId: opt.id,
    ...extra,
  });

  if (kind === 'correct') {
    // Ask the reachability derivation, not the shape of its lists. Reading "no required options" as
    // "unreachable" was true for multi-select and wrong for single-select, where `required` was
    // empty by construction — so a perfectly ordinary one-correct-answer MCQ announced that its own
    // Correct state could never fire.
    if (!isReachable(reach, 'correct')) return [{ kind: 'unreachable' }];
    const { required, forbidden, optional } = reach.correct;
    if (!allowMultiple) return joinChips(required.map(o => chip(o, 'correct')), 'or');
    /*
     * A TERM'S TONE IS ITS STATE'S, and the slash is what carries the negation.
     *
     * The forbidden options were drawn `incorrect` -- red, with a cross -- because they are
     * wrong options. Inside the CORRECT condition that reads as a contradiction: "Final
     * horizontal position, crossed out, and marked wrong" is a term of what it means to get
     * this question right, and leaving it unticked is the correct thing to do. Reported as
     * exactly that.
     *
     * The rule is the one `chipEvaluationFor` already states for every other block -- tone is
     * a property of the state, not of the option's role -- and `dropdownChips` follows it. MCQ
     * diverged only on its two NEGATED terms, in both directions (see the catch-all below), and
     * no test defended either: the colour was carrying "which role is this option", which is
     * not a question the card it sits in is asking.
     */
    const conjunction = [
      ...required.map(o => chip(o, 'correct')),
      ...forbidden.map(o => chip(o, 'correct', { slashed: true })),
    ];
    return [
      ...joinChips(conjunction, 'and'),
      ...optional.map(o => chip(o, 'neutral', { muted: true, tag: t('blocks.mcq.triggerOptional') })),
    ];
  }

  if (mode !== 'graded') {
/**
     * The catch-all's own list, outside graded: every untargeted option.
     *
     * The BLANK is not listed here. It is not an option, every block's catch-all catches it, and
     * this function once added its own — which, once `catchAllChips` took the job on for all of
     * them, meant MCQ named it twice.
     *
     * NEUTRAL throughout: roles are not consulted outside graded, so a leftover `incorrect` from
     * a mode the block is no longer in must not put a ✗ in this row.
     */
    const catchAll = reach.uncoveredWrong.map(opt => chip(opt, 'neutral'));
    return catchAll.length === 0 ? [{ kind: 'unreachable' }] : joinChips(catchAll, 'or');
  }

  // Everything that still falls through to the catch-all: a non-correct option nobody targeted, and
  // (multi-select only) a correct option left OUT that nobody targeted. The second reads as the
  // correct chip with a line through it — the answer that should have been there and was not.
  // Same rule, the other way round: a correct option LEFT OUT is why this incorrect state
  // fired, so it is a fault and wears the state's tone. It was drawn `correct` -- green, with a
  // tick -- on the card that catches wrong answers.
  const items = [
    ...reach.uncoveredWrong.map(opt => chip(opt, opt.role === 'neutral' ? 'neutral' : 'incorrect')),
    ...reach.uncoveredAbsent.map(opt => chip(opt, 'incorrect', { slashed: true })),
  ];
  if (items.length === 0) return [{ kind: 'unreachable' }];
  return joinChips(items, 'or');
}

// ─── Numerical ───────────────────────────────────────────────────────────────
//
// The largest of the eleven, because Numerical is the only block with UNITS. One authored answer has
// to be restated in every unit a student may answer in, so `computeBarEntries` fans it out — and
// the chips and the answer bar both read that fan-out. They are one computation with two consumers,
// which is why the bar's half travels with the chips rather than being left behind.

interface BarEntry  { preview: string; lo: number; hi: number; unit: string }
interface BarEntries { entries: BarEntry[]; incompleteCount: number }
interface SymbolicAnswerLabel { node: React.ReactNode; inert?: boolean }

function joinWithConnector(items: string[], connector: string): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} ${connector} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, ${connector} ${items[items.length - 1]}`;
}

export function formulasFromData(d: NumericalChipData): string[] {
  /* `needsSuppliedValue`, not `hasVariableChip`: a field quoting an earlier ANSWER is as
     unjudgeable without a value as one quoting a variable, and reading only the second meant
     `numericalCorrectChips` never took its symbolic branch for a recall — so the answer bar
     tried to resolve an equation it could not and printed "No Valid Answer" over a good one. */
  if (d.conditionType === 'exact')
    return d.answers.filter(a => needsSuppliedValue(a.value)).map(a => a.value);
  if (d.conditionType === 'between')
    return d.betweenAnswers
      .filter(a => needsSuppliedValue(a.low) || needsSuppliedValue(a.high))
      .map(a => needsSuppliedValue(a.low) && needsSuppliedValue(a.high) ? `${a.low}  to  ${a.high}` : needsSuppliedValue(a.low) ? a.low : a.high);
  return needsSuppliedValue(d.threshold) ? [d.threshold] : [];
}

export function numericalHasAnswerText(fields: NumericalChipData): boolean {
  return fields.conditionType === 'exact'
    ? fields.answers.some(a => a.value.trim() !== '')
    : fields.conditionType === 'between'
      ? fields.betweenAnswers.some(a => a.low.trim() !== '' || a.high.trim() !== '')
      : fields.threshold.trim() !== '';
}

function literalAnswerBand(
  d: NumericalChipData, a: NumericalExactAnswer,
  t: (k: string, o?: Record<string, unknown>) => string, locale: string,
): string | null {
  const v = parseFloat(a.value);
  if (!Number.isFinite(v)) return null;
  const asym = answerSymmetry(a, d) === 'asymmetric';
  const rawAbove = parseFloat((asym ? a.toleranceAbove : a.toleranceValue) || '0');
  const rawBelow = parseFloat((asym ? a.toleranceBelow : a.toleranceValue) || '0');
  if (!Number.isFinite(rawAbove) || !Number.isFinite(rawBelow) || rawAbove < 0 || rawBelow < 0) return null;
  const pct = answerToleranceMode(a, d, 'absolute') === 'percentage';
  const above = pct ? Math.abs(v) * rawAbove / 100 : rawAbove;
  const below = pct ? Math.abs(v) * rawBelow / 100 : rawBelow;
  const fmt = (n: number) => fmtAtSigFigs(n, locale, d.sigFigsRequired ? d.sigFigsCount : undefined, d.requireExponentialNotation);
  if (above === 0 && below === 0) return fmt(v);
  // `blocks.answers.acceptedRange`, the same key every other band on every other block reads — not
  // a literal " to ", which would be untranslated English in four locales' worth of surfaces.
  return t('blocks.answers.acceptedRange', { low: fmt(v - below), high: fmt(v + above), unit: '' });
}

function symbolicToleranceSuffix(
  fields: NumericalChipData, a: NumericalExactAnswer, locale: string,
): string {
  const pct = answerToleranceMode(a, fields, 'absolute') === 'percentage' ? '%' : '';
  /*
   * THROUGH `fmtLocale`, like every other number the app prints.
   *
   * This printed the raw string, so a tolerance of 0.0000005 read as `{n} ± 0.0000005` while
   * the value beside it — and the same tolerance in a resolved band, a few lines up in
   * `literalAnswerBand` — came out as `5e-7`. One chip, two notations, decided by whether the
   * answer happened to contain a variable.
   *
   * A tolerance that is not a number is left exactly as typed: the field takes plain numbers,
   * but a half-finished one should read back as what is in it rather than as `NaN`.
   */
  const fmt = (raw: string) => {
    /*
     * `Number`, not `parseFloat`. `parseFloat('1e')` is 1 — it stops at the first character it
     * cannot use — so a half-typed exponent would have been printed back as a DIFFERENT number
     * while the teacher was still typing it. `Number` says NaN, and NaN falls through to the
     * raw text below, which is what a partial entry should read as.
     */
    const n = Number(raw);
    return raw.trim() !== '' && Number.isFinite(n)
      ? fmtLocale(n, locale, fields.sigFigsRequired ? fields.sigFigsCount : undefined,
        fields.requireExponentialNotation)
      : raw;
  };
  if (answerSymmetry(a, fields) === 'asymmetric') {
    const above = a.toleranceAbove || '0', below = a.toleranceBelow || '0';
    if (above === '0' && below === '0') return '';
    return ` (+${fmt(above)}${pct} / −${fmt(below)}${pct})`;
  }
  const err = a.toleranceValue || '0';
  return err === '0' ? '' : ` ± ${fmt(err)}${pct}`;
}

function hasPartialConfig(fields: NumericalChipData): boolean {
  const hasUnit  = fields.unitMode === 'withUnits' && fields.canonicalUnit !== '';
  const hasValue = fields.conditionType === 'exact'    ? fields.answers.some(a => a.value !== '')
    : fields.conditionType === 'between' ? fields.betweenAnswers.some(a => a.low !== '' || a.high !== '')
    : fields.threshold !== '';
  return hasUnit || hasValue;
}

export function numericalRangeConfig(d: NumericalChipData, vals: Record<string, string>) {
  // Each field in ITS OWN mode — `d.angleMode` is only the seed for one never set. A single mode
  // here is how `sin(pi/2)` kept reading 0.027 after the toggle said radians.
  const r = (val: string, fieldId: string): number =>
    evaluateFormulaRaw(val, vals, angleModeFor(d, fieldId));
  return {
    conditionType: d.conditionType,
    answers: d.answers.map(a => ({
      id: a.id,
      value: r(a.value, `${a.id}.value`),
      toleranceValue: parseFloat(a.toleranceValue || '0'),
      toleranceAbove: parseFloat(a.toleranceAbove || '0'),
      toleranceBelow: parseFloat(a.toleranceBelow || '0'),
      // Carried, never defaulted — see `numericalEvaluatorData`. A chip derived from the block's
      // stale fallback is a chip that disagrees with the grader, which is the one disagreement
      // `numerical-agreement.test.ts` exists to catch.
      toleranceSymmetry: a.toleranceSymmetry,
      toleranceMode: a.toleranceMode,
    })),
    betweenAnswers: d.betweenAnswers.map(a => ({ id: a.id, low: r(a.low, a.id), high: r(a.high, a.id) })),
    toleranceMode: d.toleranceMode,
    toleranceSymmetry: d.toleranceSymmetry,
    threshold: r(d.threshold, 'threshold'),
  };
}

function symbolicAnswerLabels(
  d: NumericalChipData, t: (k: string, o?: Record<string, unknown>) => string, locale: string,
): SymbolicAnswerLabel[] {
  const unit = d.unitMode === 'withUnits' && d.canonicalUnit ? ` ${d.canonicalUnit}` : '';
  // `{}` — unrolled. This function only ever runs when there is no scenario to roll against.
  const chipify = (text: string, values: string[]) => chipifyFormulaValues(text, values, {});

  if (d.conditionType === 'exact') {
    /*
     * A LITERAL answer states what it RESOLVES TO; only an unresolved variable keeps the `± tol`.
     *
     * The answer bar's job is to tell a teacher what their setup actually covers. They already know
     * they typed 100 ± 2 — repeating it back says nothing, while "98 to 102" is the fact they came
     * to check. With a variable there is no band to print yet, so the equation and its tolerance
     * are all there is to show, and the `±` stays.
     *
     * Deliberately per ANSWER, not per block: a mixed set shows "98 to 102" beside "{n} ± 3%",
     * each answer read in whichever way it can be.
     */
    return d.answers.filter(a => a.value.trim() !== '').map((a, i) => {
      if (!needsSuppliedValue(a.value)) {
        const band = literalAnswerBand(d, a, t, locale);
        if (band) return { node: <span key={i}>{band}{unit}</span> };
      }
      return {
        node: (
          <span key={i}>
            <FormulaWithValues formula={a.value} rolledValues={{}} />{symbolicToleranceSuffix(d, a, locale)}{unit}
          </span>
        ),
      };
    });
  }
  if (d.conditionType === 'between') {
    return d.betweenAnswers
      .filter(a => a.low.trim() !== '' || a.high.trim() !== '')
      // `?` marks WHICH bound is missing, which is useful — but the row is also flagged `inert`,
      // because the evaluator drops it and it therefore accepts nothing. Without that flag the `?`
      // read as an unfinished answer that still counted.
      .map((a, i) => ({
        node: <span key={i}>{chipify(
          t('blocks.answers.acceptedRange', { low: a.low || '?', high: a.high || '?', unit }),
          [a.low, a.high])}</span>,
        inert: (a.low.trim() === '') !== (a.high.trim() === ''),
      }));
  }
  const key = ({ lt: 'acceptedPreviewLt', lte: 'acceptedPreviewLte',
                 gt: 'acceptedPreviewGt', gte: 'acceptedPreviewGte' } as const)[d.conditionType];
  if (!key || d.threshold.trim() === '') return [];
  return [{ node: <span key='t'>{chipify(
    t(`blocks.numerical.${key}`, { value: d.threshold, unit }), [d.threshold])}</span> }];
}

/**
 * A number at a required figure count, SPELLED SO IT STILL CARRIES THAT COUNT.
 *
 * Standard notation cannot express a trailing zero before the point — `450` is two significant
 * figures where `450.` is three — and a locale thousands separator adds one, because
 * `countSigFigs('1,230')` is 4. A chip is a claim about which submissions are accepted, so a
 * chip spelled `450` for a three-figure question advertises a submission the grader rejects.
 *
 * Escalates to scientific only where the plain spelling misstates the count: `9.80` stays
 * `9.80`, `450` becomes `4.50e2`. Scientific is the one form that always carries its figures,
 * which is why the convention reaches for it too.
 */
function fmtAtSigFigs(
  n: number, locale: string, sigFigs: number | undefined, forceScientific: boolean,
): string {
  const plain = fmtLocale(n, locale, sigFigs, forceScientific);
  if (sigFigs === undefined || forceScientific || !Number.isFinite(n) || n === 0) return plain;
  /* Counted WITHOUT the group separators, because a student types the digits and not the commas
     — and `countSigFigs('1,230')` is 4 where `countSigFigs('1230')` is 3. Judging the spelling as
     displayed would have let `1,230` stand for a three-figure answer that fails the moment it is
     typed back. */
  const asTyped = plain.replace(/[^0-9.eE+-]/g, '');
  return countSigFigs(asTyped) === sigFigs ? plain : fmtLocale(n, locale, sigFigs, true);
}

export function computeBarEntries(
  fields: NumericalChipData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  rolledValues: Record<string, string> = {},
): BarEntries | null {
  const fmt = (n: number) => fmtAtSigFigs(
    n, locale,
    fields.sigFigsRequired ? fields.sigFigsCount : undefined,
    fields.requireExponentialNotation,
  );
  /**
   * A BAND EDGE, ROUNDED INWARD — see `boundAtSigFigs`.
   *
   * `fmt` alone rounds to nearest, which on a LOWER bound rounds out of the band: 9.7934 at four
   * figures printed 9.793, so this legend advertised a value the grader rejects while the
   * Incorrect chip beside it drew `< 9.793` and contradicted it about that same number. The `lo`
   * and `hi` carried on the entry stay exact — only what is PRINTED moves, because only the
   * printed form is a claim about which submissions are accepted.
   */
  const bound = (n: number, dir: 'up' | 'down') =>
    fmt(fields.sigFigsRequired && Number.isFinite(n)
      ? boundAtSigFigs(n, fields.sigFigsCount, dir) : n);

  // Generate previews for a given unit label and scale factor.
  // For multi-answer conditions (exact/between), skips incomplete answers rather than
  // failing the whole unit — the caller collects the skip count and surfaces it as a warning.
  // `unit`, not `unitLabel`: this only ever receives a SUBMITTED unit (the canonical one or an
  // alternative it converts to). Display mode's `fields.unitLabel` is a label and never reaches
  // here — sharing the name is how the two came to be treated as one thing.
  function previewsForUnit(
    unit: string, scale: number, offset = 0,
  ): { previews: BarEntry[]; skipped: number } | null {
    /*
     * `± u` SITS BETWEEN THE VALUE AND THE UNIT, because that is the order the answer is written
     * in — `5.0 ± 0.1 m` — and one unit closes the whole of it. Folding it into the suffix is what
     * makes every preview carry it without each one rebuilding its own string.
     *
     * Scaled by the factor and NEVER the offset, the rule the grader already follows: an
     * uncertainty is a difference, and a difference is invariant under an offset. A 2 °C
     * uncertainty is 3.6 °F, not 39.6 °F.
     */
    const uncRaw = uncertaintyAsked(fields) ? (fields.uncertaintyValue ?? '').trim() : '';
    const uncNum = uncRaw
      ? evaluateFormulaRaw(uncRaw, rolledValues, angleModeFor(fields, 'uncertaintyValue')) * scale
      : NaN;
    /*
     * THE PLACE THE UNCERTAINTY SETS, which is what the value has to be written to — the rule
     * `precisionDisagrees` grades on, applied to what the legend advertises so the two cannot
     * disagree. Without it `5.0 ± 0.1` printed as `5 ± 0.1`, a pair the grader rejects for
     * exactly this reason: with an error box there is no sig-fig count to format against, so
     * `fmtLocale` fell back to six significant digits and dropped the trailing zero.
     *
     * Read off the SCALED uncertainty, because the place is a property of the unit it is printed
     * in: 1 m is units where 100 cm is hundreds.
     */
    /*
     * THE PLACE THE TEACHER TYPED, where they typed one.
     *
     * Deriving it from the declared figure count alone made the chip invent a compliant
     * uncertainty: `0.01` authored against a two-figure requirement came out `0.010`, so the
     * editor reported the figure count as wrong while the chip beside it showed a corrected
     * version of the same field. A chip states what is authored; the issue bar states what is
     * wrong with it.
     *
     * The count is the fallback for the two cases with no spelling to read — a FORMULA, which
     * resolves per student, and a CONVERTED unit, where the authored digits belong to another
     * scale.
     */
    const authoredPlace = (scale === 1 && !hasVariableChip(uncRaw))
      ? lastSignificantPlace(uncRaw) : null;
    const uncDecimals = !Number.isFinite(uncNum) ? null
      : authoredPlace !== null ? Math.max(0, -authoredPlace)
      : uncertaintyDecimalsFor(uncNum, fields.uncertaintySigFigs ?? 1);
    /* EXPONENT FORM WHERE THE QUESTION ASKS FOR IT. Notation governs both fields — it is a claim
       about how the answer is written, and an answer is both numbers — so a chip printing
       `5.0 ± 0.1` under it advertises a pair the grader rejects for its form. */
    const atPlace = (n: number) =>
      uncDecimals === null ? fmt(n)
        : fields.requireExponentialNotation ? atPlaceExponential(n, -uncDecimals)
        : fmtFixed(n, locale, uncDecimals);
    const uncSfx = Number.isFinite(uncNum) ? ` ± ${atPlace(uncNum)}` : '';
    const sfx = `${uncSfx}${unit ? ` ${unit}` : ''}`;
    const entry = (preview: string, lo: number, hi: number): BarEntry => ({ preview, lo, hi, unit });

    // The OFFSET goes on the value and not on the tolerance, which is why it belongs here rather
    // than in the arithmetic below: a band of ±2 °C is still ±3.6 °F wide, but its centre moves by
    // 32. Applying it once, at the point the value is converted, keeps that right for free.
    const resolveScaled = (val: string, fieldId: string): number => {
      if (!val) return NaN;
      return evaluateFormulaRaw(val, rolledValues, angleModeFor(fields, fieldId)) * scale + offset;
    };

    const fmtRange = (lo: number, hi: number) => {
      /* The band's own edges follow the uncertainty's place too. A chip reading
         `9.6 to 9.8 ± 0.13` would be stating two different precisions for one answer. */
      const flo = uncDecimals === null ? bound(lo, 'up') : atPlace(lo);
      const fhi = uncDecimals === null ? bound(hi, 'down') : atPlace(hi);
      return flo === fhi
        ? `${flo}${sfx}`
        : t('blocks.answers.acceptedRange', { low: flo, high: fhi, unit: sfx });
    };

    switch (fields.conditionType) {
      case 'exact': {
        const previews: BarEntry[] = [];
        let skipped = 0;
        for (const ans of fields.answers) {
          const v = resolveScaled(ans.value, `${ans.id}.value`);
          if (!Number.isFinite(v)) { skipped++; continue; }
          // Resolved from the ANSWER, not the block: since the tolerance settings moved onto
          // each answer, reading the block here would print a band the grader does not use.
          const abs = answerToleranceMode(ans, fields, 'absolute') === 'absolute';
          if (answerSymmetry(ans, fields) === 'asymmetric') {
            const rawAbove = parseFloat(ans.toleranceAbove || '0');
            const rawBelow = parseFloat(ans.toleranceBelow || '0');
            if (!Number.isFinite(rawAbove) || !Number.isFinite(rawBelow) || rawAbove < 0 || rawBelow < 0) { skipped++; continue; }
            if (rawAbove === 0 && rawBelow === 0) { previews.push(entry(`${atPlace(v)}${sfx}`, v, v)); continue; }
            // Absolute tolerance scales with the unit; percentage does not.
            const eAbove = abs ? rawAbove * scale : rawAbove;
            const eBelow = abs ? rawBelow * scale : rawBelow;
            const lo = abs ? v - eBelow : v * (1 - rawBelow / 100);
            const hi = abs ? v + eAbove : v * (1 + rawAbove / 100);
            previews.push(entry(fmtRange(lo, hi), lo, hi));
          } else {
            const rawErr = parseFloat(ans.toleranceValue || '0');
            if (!Number.isFinite(rawErr) || rawErr < 0) { skipped++; continue; }
            const err = abs ? rawErr * scale : rawErr;
            if (rawErr === 0) { previews.push(entry(`${atPlace(v)}${sfx}`, v, v)); continue; }
            const lo = abs ? v - err : v * (1 - rawErr / 100);
            const hi = abs ? v + err : v * (1 + rawErr / 100);
            previews.push(entry(fmtRange(lo, hi), lo, hi));
          }
        }
        return { previews, skipped };
      }
      case 'between': {
        const previews: BarEntry[] = [];
        let skipped = 0;
        for (const ans of fields.betweenAnswers) {
          const lo = resolveScaled(ans.low, ans.id), hi = resolveScaled(ans.high, ans.id);
          if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo > hi) { skipped++; continue; }
          previews.push(entry(fmtRange(lo, hi), lo, hi));
        }
        return { previews, skipped };
      }
      case 'lt':  { const v = resolveScaled(fields.threshold, 'threshold'); return Number.isFinite(v) ? { previews: [entry(t('blocks.numerical.acceptedPreviewLt',  { value: bound(v, 'down'), unit: sfx }), -Infinity, v)], skipped: 0 } : null; }
      case 'lte': { const v = resolveScaled(fields.threshold, 'threshold'); return Number.isFinite(v) ? { previews: [entry(t('blocks.numerical.acceptedPreviewLte', { value: bound(v, 'down'), unit: sfx }), -Infinity, v)], skipped: 0 } : null; }
      case 'gt':  { const v = resolveScaled(fields.threshold, 'threshold'); return Number.isFinite(v) ? { previews: [entry(t('blocks.numerical.acceptedPreviewGt',  { value: bound(v, 'up'), unit: sfx }), v, Infinity)], skipped: 0 } : null; }
      case 'gte': { const v = resolveScaled(fields.threshold, 'threshold'); return Number.isFinite(v) ? { previews: [entry(t('blocks.numerical.acceptedPreviewGte', { value: bound(v, 'up'), unit: sfx }), v, Infinity)], skipped: 0 } : null; }
    }
  }

  // Canonical unit label
  // Only `withUnits` names a unit here. Display mode's label is not a unit the previews can be
  // expressed in — there is nothing to convert to and nothing the student submits.
  const canonicalLabel = fields.unitMode === 'withUnits' ? fields.canonicalUnit : '';

  // In withUnits mode, refuse to render anything if canonical unit is absent or unrecognized.
  if (fields.unitMode === 'withUnits' && (!fields.canonicalUnit || !isValidUnit(fields.canonicalUnit))) {
    // If the teacher has already entered answer values, this is partial setup, not nothing.
    const count = fields.conditionType === 'between' ? fields.betweenAnswers.length
      : fields.conditionType === 'exact' ? fields.answers.length : 1;
    return hasPartialConfig(fields) ? { entries: [], incompleteCount: count } : null;
  }

  const canonical = previewsForUnit(canonicalLabel, 1);

  // null from previewsForUnit = one-sided condition with NaN threshold.
  // Empty previews = multi-answer condition where all answers were skipped.
  // Both cases: show "incomplete" if teacher has entered any data, else "no correct".
  if (!canonical || canonical.previews.length === 0) {
    const skipped = canonical?.skipped ?? 1;
    return hasPartialConfig(fields) ? { entries: [], incompleteCount: skipped } : null;
  }

  // Already BarEntry objects — `previewsForUnit` builds them with their bounds and unit.
  const entries: BarEntry[] = [...canonical.previews];

  // Add 'correct'-role unit alternatives, CONVERTED. They used to be previewed at scale 1, on the
  // assumption in the old comment here that an alternative unit is a synonym and the student types
  // the same number — but the unit library is explicitly about different scales too, and its own
  // rows already print "0.5 %" beside the option. So the correct bar advertised "5 %" for an answer
  // of 5 permille, which the grader then marked wrong: the legend contradicting the verdict.
  // 'neutral' units are accepted without penalty but don't fire the correct state — excluded.
  if (fields.unitMode === 'withUnits') {
    for (const opt of fields.unitOptions) {
      if (opt.role !== 'correct' || !opt.label || !isValidUnit(opt.label)) continue;
      const result = previewsForUnit(opt.label, opt.conversionFactor, opt.conversionOffset);
      if (result) entries.push(...result.previews);
    }
  }

  return { entries, incompleteCount: canonical.skipped };
}

export function numericalIncorrectChips(
  d: NumericalChipData,
  feedbackStates: FeedbackState[],
  roll: ChipRoll,
  t: (k: string, o?: Record<string, unknown>) => string,
  locale: string,
): ConditionChips {
  const chip = (label: React.ReactNode): ConditionChip => ({ kind: 'chip', label, evaluation: 'incorrect' });
  /** Same chip, carrying the interval it covers — see `intervalSourceId`. Only the GAP chips can
   *  have one: the symbolic readings above describe an unresolved equation, which no submission can
   *  be matched against. */
  const chipIn = (sourceId: string, label: React.ReactNode): ConditionChip =>
    ({ kind: 'chip', label, evaluation: 'incorrect', sourceId });

  // Nothing typed: every submission lands here, and there is nothing for one to be OTHER THAN.
  if (!numericalHasAnswerText(d)) return [chip(t('blocks.numerical.chipAnyValue'))];

  // A formula's gaps move with the dice, so with no roll they are stated symbolically rather than
  // sampled — the same rule the Correct chip follows one card up.
  const formulas = formulasFromData(d);
  if (formulas.length > 0 && roll === null) {
    const labels = symbolicAnswerLabels(d, t, locale);
    if (labels.length === 0) return [chip(t('blocks.numerical.equationPlaceholder'))];
    /*
     * "and", and the connector legitimately DIFFERS from the rolled branch below, which uses "or".
     * Both describe one region:
     *
     *   unrolled   not(x = A or x = B)  ==  x != A AND x != B
     *   rolled     the same set as gaps ==  (-inf,A) OR (A,B) OR (B,inf)
     *
     * De Morgan duals. It had NO connector at all, so a row of "not A" "not B" chips read as
     * alternatives — the reading `ConditionChipRow` warns about — which says a submission is
     * incorrect if it misses EITHER answer. That is the opposite of the rule.
     */
    // Inert rows are omitted entirely here. An ignored row accepts nothing, so its complement is
    // everything — saying "not <half a row>" would describe a constraint that does not exist.
    return joinChips(labels.filter(l => !l.inert).map((l, i) =>
      chip(<span key={i}>{t('blocks.numerical.formulaNotWord')}{' '}{l.node}</span>)), 'and');
  }

  const vals = roll ?? {};
  const ranges = computeAcceptedRangesShared(numericalRangeConfig(d, vals));
  /*
   * No guard here any more, and none needed.
   *
   * This used to bail to `gaps = []` whenever any answer produced a NaN band — and an empty gap
   * list is this function's signal for UNREACHABLE, so "cannot tell" came out as the strongest
   * claim available. A second answer row left blank was enough to trigger it: `234 ± 200%` accepts
   * [-234, 702], the blank row added [NaN, NaN], and Default Incorrect was declared dead while 1000
   * plainly reaches it.
   *
   * `computeAcceptedRanges` now drops unresolvable answers itself, so nothing NaN-shaped arrives —
   * see `blank-answer-rows.test.ts`, which holds that for all four blocks at once. Filtering again
   * here would be a second opinion about the same fact.
   */
  const valueTriggers = feedbackStates
    .filter(s => s.type === 'targeted' && !s.trigger.startsWith('unit_'))
    // Each trigger in the mode its OWN field was set to, not one mode for the whole block.
    .map(s => resolveTriggerValues(s.trigger, (val: string) =>
      evaluateFormulaRaw(val, vals, angleModeFor(d, `feedback.${s.id}.trigger`))));
  const gaps = computeDefaultIncorrectGaps(ranges, valueTriggers);
  if (gaps.length === 0) return [{ kind: 'unreachable' }];

  const unit = d.unitMode === 'withUnits' && d.canonicalUnit ? ` ${d.canonicalUnit}` : '';
  const fmt = (n: number) => fmtAtSigFigs(n, locale, d.sigFigsRequired ? d.sigFigsCount : undefined, d.requireExponentialNotation);
  /**
   * INWARD FOR THE REGION BEING DESCRIBED — which for a GAP is the other way round.
   *
   * A gap is the complement of the accepted band, so its edges are the band's edges seen from
   * outside: the accepted band lifts its lower bound to stay inside itself, and the gap below it
   * must lift its UPPER bound by the same step to stay the band's exact complement. Rounding both
   * to nearest printed "9.794 to 9.833" accepted beside "< 9.793" incorrect, leaving 9.793 in
   * neither chip while the grader put it squarely in the second.
   *
   * Done this way the two chips tile the line with no gap and no overlap, at whatever precision
   * the question is asked in.
   */
  /*
   * AT THE PRECISION THE QUESTION IS ASKED IN, which with an error box the uncertainty sets.
   *
   * `fmt` has no sig-fig count to work from here, so it fell back to six significant digits and
   * printed `< 5` for a block whose students write `5.00`. A threshold at 5 and one at 5.00 are
   * different claims about what is accepted — the same reason the band edges round inward.
   *
   * NO `± u` on these. A gap is a claim about the VALUE axis alone: `< 5.00 ± 0.10` would read
   * as a band rather than a threshold, and say something the grader does not mean. A wrong
   * uncertainty is its own axis, with its own cards.
   */
  const uncDp = uncertaintyAsked(d)
    ? (() => {
        const raw = String(d.uncertaintyValue ?? '');
        const u = parseFloat(raw);
        if (!Number.isFinite(u)) return null;
        /* The authored spelling first, for the reason the accepted chip gives: a count that
           disagrees with what was typed is an issue to report, not a number to invent. */
        const place = hasVariableChip(raw) ? null : lastSignificantPlace(raw);
        return place !== null ? Math.max(0, -place)
          : uncertaintyDecimalsFor(u, d.uncertaintySigFigs ?? 1);
      })()
    : null;
  const bound = (n: number, dir: 'up' | 'down') =>
    uncDp !== null && Number.isFinite(n)
      ? fmtFixed(n, locale, uncDp)
      : fmt(d.sigFigsRequired && Number.isFinite(n) ? boundAtSigFigs(n, d.sigFigsCount, dir) : n);
  const lower = (g: { lo: number; loInclusive: boolean }, u: string) =>
    t(g.loInclusive ? 'blocks.numerical.acceptedPreviewGte' : 'blocks.numerical.acceptedPreviewGt', { value: bound(g.lo, 'down'), unit: u });
  const upper = (g: { hi: number; hiInclusive: boolean }, u: string) =>
    t(g.hiInclusive ? 'blocks.numerical.acceptedPreviewLte' : 'blocks.numerical.acceptedPreviewLt', { value: bound(g.hi, 'up'), unit: u });

  /*
   * No isolated-point collapse any more.
   *
   * It used to detect a run of touching gaps (which is what isolated accepted points produce) and
   * return ONE chip reading "not: 1, 2, 3, 4" instead of the gaps themselves. Locally reasonable —
   * five near-identical gap chips are noisy — but it made this block the only one whose ROLLED
   * reading was collapsed while its unrolled reading was split, and it collapsed exactly the
   * commonest variable shape: two exact answers with no tolerance give two points and three
   * touching gaps, so `{a}` and `{b}` rolled to 104 and 530 printed "not 104 or 530".
   *
   * The rule now, both modes: unrolled says "not <answer>" per authored answer; rolled says what a
   * hard-coded answer would say, which for a numeric domain is the gaps — "< 104", "104 to 530",
   * "> 530". Same region, described in the vocabulary each mode can actually use.
   */

  // Gaps are alternatives — a submission lands in exactly one — so they read with "or" between.
  const canonUnit = d.unitMode === 'withUnits' && d.canonicalUnit ? d.canonicalUnit : '';
  /*
   * Nothing is accepted at all, so Default Incorrect catches EVERY submission.
   *
   * `computeDefaultIncorrectGaps` returns the whole number line for an empty accepted set, and the
   * formatter below then printed one bound of it: "<= Infinity". A number where a sentence belongs,
   * and it appeared for the commonest cause of an empty set — a `between` row with one bound
   * missing, which the evaluator drops. Same reading as the no-answer-typed case one screen up,
   * because it is the same fact: every answer lands here.
   */
  if (gaps.length === 1 && gaps[0]!.lo === -Infinity && gaps[0]!.hi === Infinity) {
    return [chip(t('blocks.numerical.chipAnyValue'))];
  }
  return joinChips(gaps.map(gap => {
    const excluded = exactFeedbackPointsInGap(gap, valueTriggers);
    return chipIn(intervalSourceId(canonUnit, gap.lo, gap.hi),
      <>
        {gap.lo === -Infinity ? upper(gap, unit)
          : gap.hi === Infinity ? lower(gap, unit)
          : `${lower(gap, '')} ${t('blocks.connectors.and')} ${upper(gap, unit)}`}
        {excluded.length > 0 && <>{' '}{t('blocks.numerical.gapExceptSuffix', {
          values: joinWithConnector(excluded.map(fmt), t('blocks.connectors.or')), unit,
        })}</>}
      </>,
    );
  }), 'or');
}

export function numericalCorrectChips(
  d: NumericalChipData, roll: ChipRoll, t: (k: string, o?: Record<string, unknown>) => string, locale: string,
): ConditionChips {
  // Nothing typed anywhere: Correct cannot fire, and neither surface should invent a condition.
  if (!numericalHasAnswerText(d)) return [{ kind: 'unreachable' }];

  // A formula answer with no roll has no single value to show — the accepted band moves with the
  // dice — so it states itself symbolically instead of picking one sample and implying it is THE
  // answer.
  const hasFormulas = formulasFromData(d).length > 0;
  if (hasFormulas && roll === null) {
    const labels = symbolicAnswerLabels(d, t, locale);
    if (labels.length === 0) {
      return [{ kind: 'chip', label: t('blocks.numerical.equationPlaceholder'), evaluation: 'correct' }];
    }
    return joinChips(labels.map(l => ({
      kind: 'chip' as const, label: l.node, evaluation: 'correct' as const,
      // Dimmed and tagged rather than drawn as an ordinary accepted band: the evaluator drops this
      // row, so presenting it like the others would claim it accepts something.
      ...(l.inert ? { muted: true, tag: t('blocks.answers.rowIgnored') } : {}),
    })), 'or');
  }

  const entries = computeBarEntries(d, t, locale, roll ?? {});
  if (!entries || entries.entries.length === 0) return [{ kind: 'unreachable' }];
  return joinChips(entries.entries.map(e => ({
    kind: 'chip' as const, label: e.preview, evaluation: 'correct' as const,
    // The band's own identity, so the legend lights the one a submission landed in. Scoped by
    // UNIT: a block with alternative units publishes one band per unit for the same answer, and a
    // submission belongs to exactly one of them.
    sourceId: intervalSourceId(e.unit, e.lo, e.hi),
  })), 'or');
}


// ─── The participation catch-all ─────────────────────────────────────────────

/** The catch-all's own chip when it has nothing specific to list against. */
/**
 * Every chip in a row, given one tone.
 *
 * Several derivations colour from a role, or from "this is the wrong half of the answer" —
 * honest in graded, wrong in a mode with nothing to be right about. The ones taking a `mode`
 * handle it themselves; GPS's and Compass's do not take one, and the PRESET reuses graded's own
 * correct-condition chips, which arrive green with a tick.
 *
 * RE-TONING, not clearing, is what the catch-all needs — see `catchAllChips`. Returning the
 * derivation's own output would let those two emit red again in a mode with nothing to be wrong
 * about, which is the leak this wrapper exists to close.
 */
export function toneChips(chips: ConditionChips, evaluation: ChipEvaluation): ConditionChips {
  return chips.map(c => (c.kind === 'chip' ? { ...c, evaluation } : c));
}

/** @see toneChips — the neutral case, which most callers want. */
export function neutralChips(chips: ConditionChips): ConditionChips {
  return toneChips(chips, 'neutral');
}

export const CATCH_ALL_SOURCE = '__catch_all__';

/**
 * What the catch-all SAYS in survey and optional.
 *
 * It enumerates what it still catches, exactly as graded's Default Incorrect does — but only once
 * there is something to contrast against. With no targeted states authored the enumeration is the
 * whole answer list plus "any other", which is a long way of writing "anything": the teacher reads
 * five chips to learn that the one card catches everything.
 *
 * So the rule is the teacher's intent, not coverage arithmetic: no targets authored means the
 * block is still a plain questionnaire and the card is simply Any Answer / Any Response. Author
 * one target and the block has become a set of choices, at which point naming the rest is the
 * useful thing.
 *
 * It took a `blankCovered` flag while optional could target a skip with `no_response` — the blank
 * was an answer the row had to name and no block's `uncoveredWrong` could list it. Optional is
 * inert now, so only survey ever enumerates and a blank there is `unanswered`, which has a rung
 * of its own.
 *
 * Stated once because all five roled blocks need it and each would otherwise decide for itself —
 * which is how the mode came to be handled five different ways in the first place.
 */
export function catchAllChips(
  mode: GradingMode,
  hasTargetedArg: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
  enumerated: () => ConditionChips,
): ConditionChips {
  /*
   * OPTIONAL CAN NEVER HAVE A TARGET, whatever the caller believes.
   *
   * `targetedFiresIn` refuses every targeted trigger in optional unconditionally, so a block in
   * that mode has no target to carve the domain up — the stored states are still there, they just
   * cannot fire. Callers computed `hasTargeted` from the STORED list and several of them forgot
   * the mode, which put this function in a state the evaluator cannot produce: it enumerated
   * leftovers around a target that never fires, and dropped the tick while doing it.
   *
   * The result was Any Response reading correct in the feedback panel and neutral on the test bar
   * of the same block, because the panel's title went through `catchAllTitle`, which has carried
   * this exact guard from the start. The two are siblings describing one rule and only one of
   * them knew it. Deciding it here rather than at four call sites is what stops the fifth.
   */
  const hasTargeted = mode !== 'optional' && hasTargetedArg;
  if (mode === 'graded' || hasTargeted) {
    /**
     * CORRECT throughout, outside graded — because that is what the answers in this row are.
     *
     * Survey means everything is acceptable; target something and THAT one becomes neutral while
     * everything else stays acceptable. So the answers the catch-all still holds are the ones
     * that satisfy the block, and now that All Correct is a positive test they are literally the
     * ones that let it fire. Green here means "this block is satisfied", which is the same thing
     * it means in graded.
     *
     * It was neutral, on the argument that "once a target exists, an answer the teacher singled
     * out would render neutral while an answer they never considered rendered correct, which
     * affirms exactly the wrong one". That was written while green meant "this is the right
     * answer" — meaningless on a survey. It now means "satisfies All Correct", and under that
     * reading the asymmetry it feared is simply true.
     *
     * THE RUNTIME ALREADY DID THIS. `participationOutcome` returns the catch-all unmodified, so
     * it keeps its stored `evaluation: 'correct'` and the student has always seen the tick;
     * `displayEvaluation` neutralises the editor alone. This brings the authoring view into line
     * rather than introducing a new meaning.
     *
     * RE-TONED, NOT LEFT ALONE. Returning `enumerated()` bare would let GPS's and Compass's gap
     * chips arrive red again — their derivations take no `mode` and colour from a correctness
     * notion this mode does not have. Forced here so no caller has to remember, and so the card
     * and its chips cannot disagree about tone.
     */
    return mode === 'graded' ? enumerated() : toneChips(enumerated(), 'correct');
  }

  return [{
    kind: 'chip',
    label: t(mode === 'optional'
      ? 'blocks.feedbackDefaults.anyResponseTitle'
      : 'blocks.feedbackDefaults.anyAnswerTitle'),
    /**
     * The tick survives here and ONLY here.
     *
     * A plain questionnaire still means "you answered, carry on", which is worth saying and is
     * what every existing survey block says today. What it must not do is outlive the moment the
     * block becomes a set of choices: once a target exists, an answer the teacher singled out
     * would render neutral while an answer they never considered rendered correct, which affirms
     * exactly the wrong one. So the enumerated branch above is neutral throughout, and the two
     * cases are decided in one place rather than per block.
     *
     */
    evaluation: 'correct',
    sourceId: CATCH_ALL_SOURCE,
  }];
}

/**
 * Which states the TEST BAR lists, and which one is the catch-all it fans out separately.
 *
 * The bar and the feedback panel are supposed to say the same thing about the same block — the
 * whole reason the chip derivations above are shared. The selection of WHICH states to draw was
 * not shared, and in survey and optional it listed the catch-all and nothing else: targeted
 * states fired, the panel showed them, and the bar still read "Any Answer". The teacher's only
 * live view of what their block does was the one surface that had not been told about the change.
 *
 * Three blocks held byte-identical copies of the selection, which is why it moved here rather
 * than being corrected three times.
 *
 * Order follows the panel: specific before general in every mode, with `unanswered` leading where
 * it can fire at all. Optional is a single row — see below.
 *
 * @returns `listed` for the one-chip-per-state map every caller already does, and `catchAll`
 *   separately because it fans out into several chips through `catchAllChips`.
 */
export function legendStatesFor<S extends { trigger: string }>(
  mode: GradingMode,
  states: S[],
  isVisibleTargeted: (s: S) => boolean,
): { listed: S[]; catchAll: S | undefined } {
  const of = (trigger: string) => states.filter(s => s.trigger === trigger);
  const targeted = states.filter(isVisibleTargeted);
  /*
   * OPTIONAL is ONE ROW, always: Any Response and nothing else.
   *
   * It is inert — no targeted states, no gates, no skip state — so a legend with anything else on
   * it is describing states that cannot fire. It used to pass `targeted` through, which was right
   * while optional could target; now the list is structurally empty and the line is kept explicit
   * so a future caller cannot reintroduce one by handing in its own `isVisibleTargeted`.
   */
  if (mode === 'optional') {
    return { listed: [], catchAll: of('any_response')[0] };
  }
  if (mode === 'survey') {
    return { listed: [...of('unanswered'), ...targeted], catchAll: of('any_answer')[0] };
  }
  // Graded keeps its own shape: `correct` and `incorrect` are pushed by the caller, each with its
  // own fan-out, so only the leading states belong in `listed`.
  return { listed: [...of('unanswered'), ...targeted], catchAll: undefined };
}

/**
 * The catch-all CARD's title, outside graded.
 *
 * Two shapes, same rule as `catchAllChips`: "Any Answer" is only true while it is the only thing
 * that can fire, so beside a list of answers the teacher singled out it becomes the OTHER answers
 * — which is exactly what graded's equivalent has always been called, Default Incorrect, not
 * Default.
 *
 * It owns the title in BOTH shapes, and that is the fix for a real bug. It used to return
 * undefined when nothing was targeted, leaving the call site's `?? getTitle(state)` to read the
 * STORED title — and a state's stored title is whatever the mode it was authored in called it. A
 * block built in graded and switched to optional kept a card headed "Correct", in a mode that has
 * no such thing. Every block in the database carries that stale title today.
 *
 * Still undefined in graded, where the stored title is the right one and the call sites read
 * `catchAllTitle(...) ?? getTitle(state)` unchanged.
 */
/**
 * Ordering's answer is an ORDER, not an answer, so its catch-all is "Any Other Order".
 *
 * Here rather than in the block because Flow names the same card and must not invent a second
 * name for it — see `catchAllTitleForBlock`.
 */
export const ORDERING_SURVEY_CATCH_ALL = 'blocks.ordering.anyOtherOrderTitle';

/**
 * Whether numerical's SETUP intercepts answers before the catch-all sees them.
 *
 * Numerical is the one block whose catch-all narrows for a reason other than a targeted state:
 * a required notation, sig-fig count or unit traps answers on its own, so "Any Answer" would be
 * an over-claim even with nothing targeted. Structural parameter rather than `NumericalBlockData`
 * so this can sit in the lib that owns `catchAllTitle`, which is what lets Flow ask the same
 * question the block asks.
 */
export function numericalHasFormatGate(d: {
  gradingMode: GradingMode;
  requireExponentialNotation: boolean;
  sigFigsRequired: boolean;
  unitMode: string;
  canonicalUnit: string;
}): boolean {
  if (d.gradingMode === 'optional') return false;
  return d.requireExponentialNotation || d.sigFigsRequired
    || (d.unitMode === 'withUnits' && !!d.canonicalUnit);
}

export function catchAllTitle(
  mode: GradingMode,
  hasTargeted: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /**
   * A block whose answer is not an "answer". Ordering's is an ORDER, so its survey catch-all is
   * "Any Other Order". Survey only, since optional has no "other" shape to name.
   */
  surveyKey = 'blocks.feedbackDefaults.anyOtherAnswerTitle',
): string | undefined {
  if (mode === 'graded') return undefined;
  // Optional is INERT and can never have a target, so it has no "other" shape — see
  // `targetedFiresIn`. Its catch-all is the only card it has, and Any Response is its name.
  if (mode === 'optional') return t('blocks.feedbackDefaults.anyResponseTitle');
  return t(hasTargeted ? surveyKey : 'blocks.feedbackDefaults.anyAnswerTitle');
}
